import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Modal, ModalBody, Spinner } from 'reactstrap';
import { useHistory } from 'react-router-dom';
import { useDateFilter } from '../../contexts/DateFilterContext';
import { useChat } from '../../contexts/ChatContext';
import axios from 'axios';
import config from '../../config';
import { fetchVoiceMktCustomerHistory } from '../../utils/marketingApi';
import styles from './CustomerDetailsModal.module.scss';
import { parseVoiceCallEntries } from './voiceCallSummary';
import {
  buildTrackerStyleFromCrmEntry,
  buildTrackerStyleSummary,
} from './voiceBotTrackerSummary';

const EMPTY_INVOICE_EDIT_ALL_FIELDS = {
  invoice_number: '',
  invoice_date: '',
  total_amount: '',
  discount_amount: '',
  sub_total: '',
  sgst: '',
  cgst: '',
  total_value: '',
  consigner_name: '',
  gstin: '',
  company_name: '',
  company_address: '',
  pickup_address: '',
  drop_off_address: '',
  job_site_address: '',
};

const invoiceEditAllFieldDefs = (isHourly) => ([
  { key: 'invoice_number', label: 'Invoice Number' },
  { key: 'invoice_date', label: 'Date' },
  { key: 'total_amount', label: 'Total Amount' },
  { key: 'discount_amount', label: 'Discount Amount' },
  { key: 'sub_total', label: 'Sub Total' },
  { key: 'sgst', label: 'SGST' },
  { key: 'cgst', label: 'CGST' },
  { key: 'total_value', label: 'Total Value' },
  ...(isHourly
    ? [{ key: 'job_site_address', label: 'Job Site Address' }]
    : [
      { key: 'pickup_address', label: 'Pickup Address' },
      { key: 'drop_off_address', label: 'Drop Off Address' },
    ]),
  { key: 'consigner_name', label: 'Consigner Name' },
  { key: 'gstin', label: 'GSTIN' },
  { key: 'company_name', label: 'Company Name' },
  { key: 'company_address', label: 'Company Address' },
]);

const fieldsFromInvoiceData = (data) => {
  const next = { ...EMPTY_INVOICE_EDIT_ALL_FIELDS };
  if (!data) return next;
  Object.keys(next).forEach((key) => {
    next[key] = data[key] != null ? String(data[key]) : '';
  });
  return next;
};

const PREFERRED_LANGUAGE_OPTIONS = ['english', 'kannada', 'roman_kannada'];
const DOMAIN_OPTIONS = [
  'AGRICULTURE',
  'FISHERIES & COASTAL',
  'FOOD & BEVERAGE',
  'CONSTRUCTION & WOODWORKS',
  'INDUSTRIAL & ENGINEERING',
  'ELECTRONICS',
  'AUTOMOTIVE',
  'APPAREL & GENERAL RETAIL',
  'HEALTHCARE & MEDICAL',
  'EVENTS & HOSPITALITY',
  'EDUCATION & INSTITUTIONS',
  'SERVICES, ADVERTISING',
  'OTHERS',
];

const isPreferredLanguageOption = (value = '') =>
  PREFERRED_LANGUAGE_OPTIONS.includes(String(value).toLowerCase());

const isDomainOption = (value = '') =>
  DOMAIN_OPTIONS.includes(String(value || '').trim());

const LEAD_STATUS_STYLES = {
  new: { bg: '#dbeafe', color: '#1e40af', label: 'New' },
  follow_up: { bg: '#ede9fe', color: '#6d28d9', label: 'Follow up' },
  converted: { bg: '#d1fae5', color: '#065f46', label: 'Converted' },
  contacted: { bg: '#fef3c7', color: '#92400e', label: 'Contacted' },
};

const LEAD_TYPE_LABELS = {
  part_load_interest: 'Part Load',
  future_booking: 'Future Booking',
  general_inquiry: 'General Inquiry',
  equipment_interest: 'Equipment',
  incomplete_booking: 'Incomplete Booking',
};

function normalizeCustomerTaxonomyResponse(data) {
  if (!data) return {};
  if (data.taxonomy && typeof data.taxonomy === 'object' && !Array.isArray(data.taxonomy)) {
    return data.taxonomy;
  }
  const keys = Object.keys(data);
  if (keys.length === 1 && keys[0] === 'detail') return {};
  return data;
}

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

// Loading State
const LoadingState = () => (
  <div className={styles.loadingState}>
    <div className={styles.spinner}></div>
    <p>Loading customer details...</p>
  </div>
);

// Error State
const ErrorState = ({ error, onRetry }) => (
  <div className={styles.errorState}>
    <div className={styles.errorIcon}>
      <i className="fa fa-exclamation-triangle"></i>
    </div>
    <p className={styles.errorMessage}>Error: {error}</p>
    <button className={styles.retryBtn} onClick={onRetry}>
      <i className="fa fa-refresh"></i> Try Again
    </button>
  </div>
);

// InfoRow for displaying customer information
const InfoRow = ({ label, value, renderValue, children }) => (
  <tr>
    <td className={styles.infoLabel}>{label}</td>
    <td className={styles.infoValue}>
      {children || (renderValue ? renderValue() : (value || 'N/A'))}
    </td>
  </tr>
);

// BookingCard component for displaying booking information
const BookingCard = ({ booking }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [invoiceSending, setInvoiceSending] = useState(false);
  const [invoiceSent, setInvoiceSent] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);
  const [invoiceError, setInvoiceError] = useState(null);
  const [invoiceEditAllUnlocked, setInvoiceEditAllUnlocked] = useState(false);
  const [invoiceEditAllPrompt, setInvoiceEditAllPrompt] = useState(false);
  const [invoiceEditAllPassword, setInvoiceEditAllPassword] = useState('');
  const [invoiceEditAllPasswordInput, setInvoiceEditAllPasswordInput] = useState('');
  const [invoiceEditAllDownloading, setInvoiceEditAllDownloading] = useState(false);
  const [invoiceEditAllPreviewUpdating, setInvoiceEditAllPreviewUpdating] = useState(false);
  const [invoiceEditAllFields, setInvoiceEditAllFields] = useState({ ...EMPTY_INVOICE_EDIT_ALL_FIELDS });
  const invoiceEditAllPreviewTimer = useRef(null);
  const invoiceEditAllPreviewSeq = useRef(0);
  const invoiceEditAllSkipLive = useRef(true);

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'completed': 'success',
      'confirmed': 'primary',
      'cancelled': 'danger',
      'pending': 'warning',
      'Assigned': 'info',
      'in_progress': 'info'
    };
    const color = statusColors[status] || 'secondary';
    return <span className={`${styles.badge} ${styles[color]}`}>{status || 'unknown'}</span>;
  };

  const getPaymentStatusBadge = (paymentId) => {
    if (paymentId) {
      return <span className={`${styles.badge} ${styles.success}`}>
        <i className="fa fa-check-circle"></i> Paid
      </span>;
    } else {
      return <span className={`${styles.badge} ${styles.danger}`}>
        <i className="fa fa-clock-o"></i> Pending
      </span>;
    }
  };

  const isPaid = booking.payment_id != null && String(booking.payment_id).trim() !== '';
  const isCancelled = booking.state === 'cancelled';
  const canGenerateInvoice = isPaid && !isCancelled;

  const handleInvoiceClick = async (e) => {
    e.stopPropagation();
    setShowInvoiceModal(true);
    setInvoiceLoading(true);
    setInvoiceError(null);
    setInvoiceData(null);
    setInvoiceSent(false);
    setInvoiceEditAllUnlocked(false);
    setInvoiceEditAllPrompt(false);
    setInvoiceEditAllPassword('');
    setInvoiceEditAllPasswordInput('');
    setInvoiceEditAllDownloading(false);
    setInvoiceEditAllFields({ ...EMPTY_INVOICE_EDIT_ALL_FIELDS });

    try {
      const resp = await fetch(`${config.customerApiBase}/bookings/${booking.id}/invoice`, {
        method: 'POST',
      });
      const data = await resp.json();
      if (!resp.ok) {
        throw new Error(data.detail || 'Failed to generate invoice');
      }
      setInvoiceData(data);
    } catch (err) {
      setInvoiceError(err.message || 'Error generating invoice');
    } finally {
      setInvoiceLoading(false);
    }
  };

  const handleDownload = (e) => {
    e.stopPropagation();
    if (invoiceData?.invoice_url) {
      window.open(invoiceData.invoice_url, '_blank');
    }
  };

  const handleEditAllClick = (e) => {
    e.stopPropagation();
    if (invoiceEditAllUnlocked) {
      setInvoiceEditAllUnlocked(false);
      setInvoiceEditAllPrompt(false);
      return;
    }
    setInvoiceEditAllPrompt(true);
    setInvoiceEditAllPasswordInput('');
    setInvoiceError(null);
  };

  const handleEditAllUnlock = async (e) => {
    e.stopPropagation();
    if (!booking?.id || !invoiceEditAllPasswordInput) return;
    setInvoiceLoading(true);
    setInvoiceError(null);

    try {
      const resp = await fetch(`${config.customerApiBase}/bookings/${booking.id}/invoice/local-preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          password: invoiceEditAllPasswordInput,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data.detail || 'Failed to unlock Edit All');
      }
      setInvoiceEditAllPassword(invoiceEditAllPasswordInput);
      setInvoiceEditAllPasswordInput('');
      setInvoiceEditAllUnlocked(true);
      setInvoiceEditAllPrompt(false);
      invoiceEditAllSkipLive.current = true;
      setInvoiceEditAllFields(fieldsFromInvoiceData(data.invoice_data));
      setInvoiceData(prev => ({ ...prev, invoice_html: data.invoice_html, invoice_data: { ...(prev?.invoice_data || {}), ...data.invoice_data } }));
    } catch (err) {
      setInvoiceError(err.message || 'Invalid password');
    } finally {
      setInvoiceLoading(false);
    }
  };

  const handleEditAllField = (field, value) => {
    setInvoiceEditAllFields(prev => ({ ...prev, [field]: value }));
  };

  const refreshEditAllPreview = useCallback(async (fields, { silent = true } = {}) => {
    if (!booking?.id || !invoiceEditAllPassword) return;
    const seq = ++invoiceEditAllPreviewSeq.current;
    if (!silent) {
      setInvoiceLoading(true);
      setInvoiceError(null);
    } else {
      setInvoiceEditAllPreviewUpdating(true);
    }

    try {
      const resp = await fetch(`${config.customerApiBase}/bookings/${booking.id}/invoice/local-preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          password: invoiceEditAllPassword,
          ...fields,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        throw new Error(data.detail || 'Failed to update preview');
      }
      if (seq !== invoiceEditAllPreviewSeq.current) return;
      setInvoiceData(prev => ({ ...prev, invoice_html: data.invoice_html, invoice_data: { ...(prev?.invoice_data || {}), ...data.invoice_data } }));
    } catch (err) {
      if (seq !== invoiceEditAllPreviewSeq.current) return;
      if (!silent) setInvoiceError(err.message || 'Error updating preview');
    } finally {
      if (seq === invoiceEditAllPreviewSeq.current) {
        if (!silent) setInvoiceLoading(false);
        setInvoiceEditAllPreviewUpdating(false);
      }
    }
  }, [booking?.id, invoiceEditAllPassword]);

  const handleEditAllApplyPreview = async (e) => {
    e.stopPropagation();
    await refreshEditAllPreview(invoiceEditAllFields, { silent: false });
  };

  useEffect(() => {
    if (!invoiceEditAllUnlocked || !invoiceEditAllPassword || !booking?.id) return;
    if (invoiceEditAllSkipLive.current) {
      invoiceEditAllSkipLive.current = false;
      return;
    }
    if (invoiceEditAllPreviewTimer.current) clearTimeout(invoiceEditAllPreviewTimer.current);
    invoiceEditAllPreviewTimer.current = setTimeout(() => {
      refreshEditAllPreview(invoiceEditAllFields, { silent: true });
    }, 400);
    return () => {
      if (invoiceEditAllPreviewTimer.current) clearTimeout(invoiceEditAllPreviewTimer.current);
    };
  }, [invoiceEditAllFields, invoiceEditAllUnlocked, invoiceEditAllPassword, booking?.id, refreshEditAllPreview]);

  const handleEditAllDownload = async (e) => {
    e.stopPropagation();
    if (!booking?.id || invoiceEditAllDownloading || !invoiceEditAllPassword) return;
    setInvoiceEditAllDownloading(true);
    setInvoiceError(null);

    try {
      const resp = await fetch(`${config.customerApiBase}/bookings/${booking.id}/invoice/local-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          password: invoiceEditAllPassword,
          ...invoiceEditAllFields,
        }),
      });
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}));
        throw new Error(data.detail || 'Failed to download local PDF');
      }
      const blob = await resp.blob();
      const header = resp.headers.get('Content-Disposition');
      const match = header && header.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i);
      const filename = match ? decodeURIComponent(match[1]) : `Invoice_INV-${booking.id}.pdf`;
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setInvoiceError(err.message || 'Error downloading local PDF');
    } finally {
      setInvoiceEditAllDownloading(false);
    }
  };

  const handleSend = async (e) => {
    e.stopPropagation();
    setInvoiceSending(true);
    setInvoiceSent(false);
    setInvoiceError(null);

    try {
      const resp = await fetch(`${config.customerApiBase}/bookings/${booking.id}/invoice/send`, {
        method: 'POST',
      });
      const data = await resp.json();
      if (!resp.ok) {
        throw new Error(data.detail || 'Failed to send invoice');
      }
      setInvoiceSent(true);
      setInvoiceData(prev => ({ ...prev, ...data }));
    } catch (err) {
      setInvoiceError(err.message || 'Error sending invoice');
    } finally {
      setInvoiceSending(false);
    }
  };

  const closeInvoiceModal = (e) => {
    e.stopPropagation();
    setShowInvoiceModal(false);
    setInvoiceData(null);
    setInvoiceError(null);
    setInvoiceSent(false);
    setInvoiceEditAllUnlocked(false);
    setInvoiceEditAllPrompt(false);
    setInvoiceEditAllPassword('');
    setInvoiceEditAllPasswordInput('');
    setInvoiceEditAllDownloading(false);
    setInvoiceEditAllFields({ ...EMPTY_INVOICE_EDIT_ALL_FIELDS });
  };

  return (
    <div className={styles.bookingCard}>
      {/* Collapsed View - Summary */}
      <div className={styles.bookingHeader} onClick={() => setIsExpanded(!isExpanded)}>
        <div className={styles.bookingTitleRow}>
          <h6 className={styles.bookingTitle}>
            <i className={`fa fa-calendar-check-o ${styles.bookingIcon}`}></i>
            Booking #{booking.id}
          </h6>
          {getStatusBadge(booking.state)}
        </div>
        <div className={styles.bookingMeta} onClick={(e) => e.stopPropagation()}>
          {booking.payment_estimate && (
            <span className={`${styles.bookingPrice} ${isPaid ? styles.paid : styles.pending}`}>
              ₹{booking.payment_estimate}
            </span>
          )}
          <span className={styles.bookingDate}>
            {formatDateTime(booking.pickup_time).split(',')[0]}
          </span>
          {canGenerateInvoice && (
            <button
              className={styles.invoiceBtn}
              onClick={handleInvoiceClick}
              title="Download / Send Invoice"
            >
              <i className="fa fa-file-text-o" />
              Invoice
            </button>
          )}
          <i className={`fa fa-chevron-down ${styles.expandIcon} ${isExpanded ? styles.expanded : ''}`} />
        </div>
      </div>

      {/* Expanded View - Full Details */}
      {isExpanded && (
        <div className={styles.bookingDetails}>
          <table className={styles.infoTable}>
            <tbody>
              <InfoRow label="Pickup" value={booking.pickup_location} />
              <InfoRow label="Drop" value={booking.drop_location || 'N/A'} />
              {booking.vehicle_class_id && (
                <InfoRow label="Vehicle Class" value={`Class ${booking.vehicle_class_id}`} />
              )}
              <InfoRow label="Scheduled" value={formatDateTime(booking.pickup_time)} />
              <InfoRow label="Created" value={formatDateTime(booking.created_at)} />
              <InfoRow label="Payment Status" renderValue={() => getPaymentStatusBadge(booking.payment_id)} />
              {booking.payment_id && (
                <InfoRow
                  label="Payment ID"
                  renderValue={() => (
                    <code style={{ padding: '0.25rem 0.5rem', background: '#f1f3f5', borderRadius: '4px' }}>
                      {booking.payment_id}
                    </code>
                  )}
                />
              )}
              {booking.distance && (
                <InfoRow label="Distance" renderValue={() => <span>{booking.distance} km</span>} />
              )}
              {booking.duration && (
                <InfoRow label="Duration" renderValue={() => <span>{booking.duration} mins</span>} />
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Invoice Preview Modal */}
      {showInvoiceModal && (
        <div className={styles.invoiceModalOverlay} onClick={closeInvoiceModal}>
          <div className={styles.invoiceModal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.invoiceModalHeader}>
              <div className={styles.invoiceModalTitle}>
                <i className="fa fa-file-text-o" /> Invoice Preview
              </div>
              <button className={styles.invoiceModalClose} onClick={closeInvoiceModal}>&times;</button>
            </div>

            <div className={styles.invoiceModalBody}>
              {invoiceLoading && (
                <div style={{ textAlign: 'center', padding: '2rem', color: '#868e96' }}>
                  <i className="fa fa-spinner fa-spin" /> Generating invoice...
                </div>
              )}

              {invoiceError && (
                <div style={{ padding: '0.75rem', background: '#fff5f5', borderRadius: '8px', color: '#c62828', fontSize: '0.82rem', marginBottom: '0.5rem' }}>
                  {invoiceError}
                </div>
              )}

              {invoiceSent && (
                <div style={{ padding: '0.75rem', background: '#f0fdf4', borderRadius: '8px', color: '#2e7d32', fontSize: '0.82rem', marginBottom: '0.5rem' }}>
                  <i className="fa fa-check-circle" /> Invoice sent to customer via WhatsApp!
                </div>
              )}

              {invoiceEditAllPrompt && !invoiceEditAllUnlocked && (
                <div className={styles.editAllPanel}>
                  <div className={styles.editAllTitle}>
                    <i className="fa fa-lock" /> Enter password to edit all fields
                  </div>
                  <input
                    type="password"
                    className={styles.editAllInput}
                    value={invoiceEditAllPasswordInput}
                    onChange={(e) => setInvoiceEditAllPasswordInput(e.target.value)}
                    placeholder="Password"
                    onKeyDown={(e) => { if (e.key === 'Enter') handleEditAllUnlock(e); }}
                  />
                  <div className={styles.editAllActions}>
                    <button
                      className={styles.editAllBtn}
                      onClick={handleEditAllUnlock}
                      disabled={invoiceLoading || !invoiceEditAllPasswordInput}
                    >
                      {invoiceLoading ? (<><i className="fa fa-spinner fa-spin" /> Checking...</>) : 'Unlock'}
                    </button>
                    <button
                      className={styles.editAllCancelBtn}
                      onClick={() => { setInvoiceEditAllPrompt(false); setInvoiceEditAllPasswordInput(''); }}
                      disabled={invoiceLoading}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {invoiceData?.invoice_html && (
                <div className={styles.invoicePreviewFrame}>
                  <iframe
                    srcDoc={invoiceData.invoice_html}
                    title="Invoice Preview"
                    className={`${styles.invoiceIframe} ${invoiceEditAllUnlocked ? styles.invoiceIframeCompact : ''}`}
                    sandbox="allow-same-origin"
                  />
                </div>
              )}

              {invoiceEditAllUnlocked && (
                <div className={styles.editAllPanel}>
                  <div className={styles.editAllTitle}>
                    <span><i className="fa fa-unlock" /> Edit All Invoice Fields</span>
                    {invoiceEditAllPreviewUpdating && (
                      <span className={styles.editAllUpdating}><i className="fa fa-spinner fa-spin" /> Updating preview...</span>
                    )}
                  </div>
                  {invoiceEditAllFieldDefs((booking.booking_type || '').toLowerCase() === 'hourly').map(field => (
                    <div key={field.key} className={styles.editAllField}>
                      <label>{field.label}</label>
                      <input
                        type="text"
                        className={styles.editAllInput}
                        value={invoiceEditAllFields[field.key] || ''}
                        onChange={(e) => handleEditAllField(field.key, e.target.value)}
                      />
                    </div>
                  ))}
                  <div className={styles.editAllActions}>
                    <button
                      className={styles.editAllBtn}
                      onClick={handleEditAllApplyPreview}
                      disabled={invoiceLoading}
                    >
                      {invoiceLoading ? (<><i className="fa fa-spinner fa-spin" /> Updating...</>) : (<><i className="fa fa-eye" /> Update Preview</>)}
                    </button>
                    <button
                      className={styles.editAllCancelBtn}
                      onClick={handleEditAllClick}
                      disabled={invoiceLoading}
                    >
                      Close Edit All
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className={styles.invoiceActions}>
              <button
                className={styles.downloadBtn}
                onClick={handleDownload}
                disabled={invoiceLoading || !invoiceData?.invoice_url}
              >
                <i className="fa fa-download" /> Download PDF
              </button>
              <button
                className={styles.editAllBtn}
                onClick={invoiceEditAllUnlocked ? handleEditAllDownload : handleEditAllClick}
                disabled={invoiceLoading || invoiceEditAllDownloading || !booking?.id}
              >
                {invoiceEditAllDownloading ? (
                  <><i className="fa fa-spinner fa-spin" /> Downloading...</>
                ) : invoiceEditAllUnlocked ? (
                  <><i className="fa fa-download" /> Download Local PDF</>
                ) : (
                  <><i className="fa fa-lock" /> Edit All</>
                )}
              </button>
              <button
                className={styles.sendBtn}
                onClick={handleSend}
                disabled={invoiceLoading || invoiceSending || !invoiceData?.invoice_url}
              >
                {invoiceSending ? (
                  <><i className="fa fa-spinner fa-spin" /> Sending...</>
                ) : (
                  <><i className="fa fa-whatsapp" /> Send via WhatsApp</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// LeadCard component for displaying a single active lead
const LeadCard = ({ lead, history, onOpenLead }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const statusStyle = LEAD_STATUS_STYLES[lead.status] || { bg: '#f3f4f6', color: '#374151', label: lead.status };
  const leadTypeLabel = LEAD_TYPE_LABELS[lead.lead_type] || lead.lead_type;

  const handleOpenInLeads = () => {
    if (onOpenLead) {
      onOpenLead(lead.id);
    } else if (history) {
      history.push(`/app/leads?expandLead=${lead.id}`);
    }
  };

  return (
    <div className={styles.leadCard}>
      <div
        className={styles.leadHeader}
        onClick={() => setIsExpanded(!isExpanded)}
        title="Click to view more information"
      >
        <span className={styles.leadId}>
          <i className="fa fa-hashtag"></i> Lead #{lead.id}
        </span>
        <span className={styles.leadStatus} style={{ background: statusStyle.bg, color: statusStyle.color }}>
          {statusStyle.label}
        </span>
        <i className={`fa fa-chevron-${isExpanded ? 'up' : 'down'} ${styles.leadExpandIcon}`}></i>
      </div>
      <div className={styles.leadMeta}>
        {leadTypeLabel && (
          <span className={styles.leadMetaItem}>
            <i className="fa fa-tag"></i> {leadTypeLabel}
          </span>
        )}
        {(lead.pickup_locality || lead.pickup_city) && (
          <span className={styles.leadMetaItem}>
            <i className="fa fa-map-marker"></i> {lead.pickup_locality}{lead.pickup_city ? `, ${lead.pickup_city}` : ''}
          </span>
        )}
      </div>
      <div className={styles.leadDates}>
        {lead.status === 'follow_up' && lead.follow_up_at ? (
          <span>
            <i className="fa fa-clock-o"></i> Follow up: {formatDateTime(lead.follow_up_at)}
          </span>
        ) : lead.contact_by ? (
          <span>
            <i className="fa fa-calendar"></i> Contact by: {formatDateTime(lead.contact_by)}
          </span>
        ) : null}
        <span>
          <i className="fa fa-calendar-check-o"></i> Created: {formatDateTime(lead.created_at)}
        </span>
      </div>
      {isExpanded && (
        <div className={styles.leadDetails}>
          {lead.remarks && (
            <div className={styles.leadDetailRow}>
              <span className={styles.leadDetailLabel}><i className="fa fa-sticky-note-o"></i> Remarks</span>
              <span className={styles.leadDetailValue}>{lead.remarks}</span>
            </div>
          )}
          {lead.vehicle_class_id && (
            <div className={styles.leadDetailRow}>
              <span className={styles.leadDetailLabel}><i className="fa fa-truck"></i> Vehicle Class</span>
              <span className={styles.leadDetailValue}>Class {lead.vehicle_class_id}</span>
            </div>
          )}
          {lead.drop_addresses && lead.drop_addresses.length > 0 && (
            <div className={styles.leadDetailRow}>
              <span className={styles.leadDetailLabel}><i className="fa fa-map-marker"></i> Drops</span>
              <span className={styles.leadDetailValue}>{lead.drop_addresses.join(' | ')}</span>
            </div>
          )}
          {(lead.customer_phone || lead.phone_number) && (
            <div className={styles.leadDetailRow}>
              <span className={styles.leadDetailLabel}><i className="fa fa-phone"></i> Phone</span>
              <span className={styles.leadDetailValue}>{lead.customer_phone || lead.phone_number}</span>
            </div>
          )}
          <button type="button" className={styles.leadViewBtn} onClick={handleOpenInLeads}>
            <i className="fa fa-external-link"></i> View in Leads
          </button>
        </div>
      )}
    </div>
  );
};

// ConversationCard component for displaying conversation information
const ConversationCard = ({
  conversation,
  relatedBooking,
  history,
  customerId,
  onOpenConversation
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const messages = Array.isArray(conversation?.messages) ? conversation.messages : [];

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getConversationStatusBadge = (success, failureReason) => {
    if (success) {
      return <span className={`${styles.badge} ${styles.success}`}>
        <i className="fa fa-check-circle"></i> Success
      </span>;
    } else {
      return <span className={`${styles.badge} ${styles.danger}`}>
        <i className="fa fa-times-circle"></i> Failed
      </span>;
    }
  };

  return (
    <div className={styles.conversationCard}>
      {/* Collapsed View - Summary */}
      <div className={styles.conversationHeader} onClick={() => setIsExpanded(!isExpanded)}>
        <div className={styles.conversationTitleRow}>
          <h6 className={styles.conversationTitle}>
            <i className={`fa fa-comments ${styles.conversationIcon}`}></i>
            Conversation #{conversation.id}
          </h6>
          {getConversationStatusBadge(conversation.success, conversation.failure_reason)}
          {conversation.booking_id && relatedBooking && (
            <span className={`${styles.badge} ${styles.primary}`}>
              <i className="fa fa-link"></i> Booking #{conversation.booking_id}
            </span>
          )}
        </div>
        <div className={styles.conversationMeta}>
          <span className={styles.messageCount}>
            <i className="fa fa-comment"></i>
            {messages.length} msgs
          </span>
          <span className={styles.conversationDate}>
            {formatDateTime(conversation.created_at).split(',')[0]}
          </span>
          <i className={`fa fa-chevron-down ${styles.expandIcon} ${isExpanded ? styles.expanded : ''}`}></i>
        </div>
      </div>

      {/* Expanded View - Full Details */}
      {isExpanded && (
        <div className={styles.conversationDetails}>
          <table className={styles.infoTable}>
            <tbody>
              <InfoRow label="Intent" value={conversation.intent} />
              <InfoRow label="Status" renderValue={() => getConversationStatusBadge(conversation.success, conversation.failure_reason)} />
              {conversation.failure_reason && (
                <InfoRow label="Failure Reason" value={conversation.failure_reason} />
              )}
              <InfoRow label="Created" value={formatDateTime(conversation.created_at)} />
              {conversation.booking_id && (
                <InfoRow
                  label="Related Booking"
                  renderValue={() => (
                    <span className={`${styles.badge} ${styles.primary}`}>
                      <i className="fa fa-calendar"></i> Booking #{conversation.booking_id}
                    </span>
                  )}
                />
              )}
              {relatedBooking && (
                <>
                  <InfoRow label="Pickup" value={relatedBooking.pickup_location} />
                  <InfoRow label="Drop" value={relatedBooking.drop_location || 'N/A'} />
                  <InfoRow label="Scheduled" value={formatDateTime(relatedBooking.pickup_time)} />
                  <InfoRow
                    label="Amount"
                    renderValue={() => (
                      <span style={{ fontWeight: '600', color: relatedBooking.payment_id ? '#2e7d32' : '#c62828' }}>
                        ₹{relatedBooking.payment_estimate}
                      </span>
                    )}
                  />
                </>
              )}
            </tbody>
          </table>

          {/* Messages Preview */}
          {messages.length > 0 && (
            <div className={styles.messagePreview}>
              <div className={styles.previewHeader}>
                <h6><i className="fa fa-comments"></i> Message Preview</h6>
                <button
                  className={styles.viewFullBtn}
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenConversation(
                      conversation.id,
                      conversation.customer_id || customerId
                    );
                  }}
                >
                  <i className="fa fa-external-link"></i> View Full Conversation
                </button>
              </div>
              <div className={styles.previewContainer}>
                {messages.slice(0, 4).map((msg, index) => {
                  const safeContent = typeof msg?.content === 'string' ? msg.content : (msg?.content != null ? String(msg.content) : '');
                  const safeTimestamp = msg?.timestamp ? new Date(msg.timestamp) : null;
                  return (
                    <div key={index} className={styles.previewMessage}>
                      <div className={styles.messageMeta}>
                        <span className={`${styles.roleBadge} ${msg.role === 'user' ? styles.customer : styles.bot}`}>
                          {msg.role === 'user' ? 'Customer' : 'Bot'}
                        </span>
                        <span className={styles.messageTime}>
                          {safeTimestamp ? safeTimestamp.toLocaleTimeString() : 'N/A'}
                        </span>
                      </div>
                      <div className={styles.messageContent}>
                        {safeContent.length > 100 ? `${safeContent.substring(0, 100)}...` : safeContent}
                      </div>
                    </div>
                  );
                })}
                {messages.length > 4 && (
                  <div className={styles.moreMessages}>
                    ... and {messages.length - 4} more messages
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// CallSummaryItem — Voice Tracker format: Summary paragraph + ## Agent/Customer/Outcome notes
const CallSummaryItem = ({ entry, onDelete, isDeleting, trackerParts }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const parts = trackerParts || buildTrackerStyleFromCrmEntry(entry);
  const whenLabel = entry.formatted || entry.time || '';
  const badge = parts.awaiting
    ? { label: 'Awaiting Vobiz', color: '#b45309', bg: '#fef3c7' }
    : parts.fallback
      ? { label: 'Transcript fallback', color: '#a16207', bg: '#fef9c3' }
      : parts.isFinal
        ? { label: 'Vobiz final', color: '#15803d', bg: '#dcfce7' }
        : null;

  return (
    <li className={styles.callSummaryItem}>
      {whenLabel ? (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <button
            type="button"
            className={styles.callSummaryWhen}
            onClick={() => setIsExpanded(!isExpanded)}
            style={{ flex: 1, textAlign: 'left' }}
          >
            <span>
              <i className="fa fa-clock-o" style={{ marginRight: '0.4rem' }} />
              {whenLabel}
            </span>
            {badge && (
              <span
                style={{
                  marginLeft: '0.5rem',
                  fontSize: '0.68rem',
                  fontWeight: 700,
                  color: badge.color,
                  background: badge.bg,
                  borderRadius: 999,
                  padding: '0.12rem 0.45rem',
                }}
              >
                {badge.label}
              </span>
            )}
            <i className={`fa fa-chevron-${isExpanded ? 'up' : 'down'}`} style={{ color: '#6366f1', marginLeft: '0.5rem' }}></i>
          </button>
          {!showConfirm && onDelete && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setShowConfirm(true); }}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: '#94a3b8', padding: '0.25rem 0.4rem', marginLeft: '0.4rem',
                borderRadius: '6px', transition: 'all 0.15s ease', fontSize: '0.8rem',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.color = '#ef4444'; e.currentTarget.style.background = '#fef2f2'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = '#94a3b8'; e.currentTarget.style.background = 'none'; }}
              title="Delete this entry"
            >
              <i className="fa fa-trash-o"></i>
            </button>
          )}
        </div>
      ) : null}
      {showConfirm && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '0.5rem',
          padding: '0.5rem 0.65rem', marginTop: '0.35rem',
          background: '#fef2f2', borderRadius: '8px', border: '1px solid #fecaca',
          width: '100%', fontSize: '0.82rem', color: '#991b1b',
        }}>
          <i className="fa fa-exclamation-triangle" style={{ color: '#ef4444' }}></i>
          <span style={{ flex: 1 }}>Delete this entry?</span>
          <button
            type="button"
            disabled={isDeleting}
            onClick={(e) => { e.stopPropagation(); if (onDelete) onDelete(entry); }}
            style={{
              background: '#ef4444', color: '#fff', border: 'none', borderRadius: '6px',
              padding: '0.3rem 0.65rem', fontSize: '0.78rem', fontWeight: 600,
              cursor: isDeleting ? 'not-allowed' : 'pointer', opacity: isDeleting ? 0.6 : 1,
            }}
          >
            {isDeleting ? <><i className="fa fa-spinner fa-spin" style={{ marginRight: '0.3rem' }}></i>Deleting</> : 'Delete'}
          </button>
          <button
            type="button"
            disabled={isDeleting}
            onClick={(e) => { e.stopPropagation(); setShowConfirm(false); }}
            style={{
              background: '#fff', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: '6px',
              padding: '0.3rem 0.65rem', fontSize: '0.78rem', fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Cancel
          </button>
        </div>
      )}
      {(!whenLabel || isExpanded) && (parts.summaryText || parts.notesMarkdown) && (
        <div className={styles.callSummaryBody}>
          {parts.summaryText && (
            <div
              style={{
                marginBottom: parts.notesMarkdown ? '0.65rem' : 0,
                padding: '0.65rem 0.75rem',
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                fontSize: '0.85rem',
                lineHeight: 1.5,
                color: '#1e293b',
              }}
            >
              <div style={{ fontSize: '0.7rem', color: '#64748b', marginBottom: 4, fontWeight: 600 }}>
                Summary
              </div>
              {parts.summaryText}
            </div>
          )}
          {parts.notesMarkdown ? renderMarkdownNotes(parts.notesMarkdown) : null}
        </div>
      )}
    </li>
  );
};

// Simple markdown renderer for call summary notes (## headings + - bullets)
// Strips the ## Summary section since it's already shown in the summary card above.
const renderMarkdownNotes = (text) => {
  if (!text) return null;
  const lines = text.split('\n');
  const elements = [];
  let bulletItems = [];
  let skipUntilNextHeading = false;

  const flushBullets = () => {
    if (bulletItems.length > 0) {
      elements.push(
        <ul key={`bullets-${elements.length}`} style={{ margin: '4px 0 8px 18px', padding: 0, listStyle: 'disc' }}>
          {bulletItems.map((b, i) => (
            <li key={i} style={{ marginBottom: 3, lineHeight: 1.4 }}>{b}</li>
          ))}
        </ul>
      );
      bulletItems = [];
    }
  };

  for (const line of lines) {
    const headingMatch = line.match(/^##\s+(.+)/);
    if (headingMatch) {
      const heading = headingMatch[1].trim().toLowerCase();
      // Match Voice Tracker: hide Summary + Ops (pipeline meta) for bot call notes
      if (heading === 'summary' || heading === 'ops') {
        flushBullets();
        skipUntilNextHeading = true;
        continue;
      }
      skipUntilNextHeading = false;
      flushBullets();
      elements.push(
        <div
          key={`h-${elements.length}`}
          style={{
            fontWeight: 700,
            fontSize: '0.8rem',
            color: '#1e40af',
            marginTop: elements.length > 0 ? 10 : 0,
            marginBottom: 2,
            borderBottom: '1px solid #e2e8f0',
            paddingBottom: 2,
          }}
        >
          {headingMatch[1].trim()}
        </div>
      );
      continue;
    }

    if (skipUntilNextHeading) continue;

    const bulletMatch = line.match(/^\s*[-*]\s+(.+)/);
    if (bulletMatch) {
      bulletItems.push(bulletMatch[1].trim());
      continue;
    }

    const trimmed = line.trim();
    if (trimmed) {
      flushBullets();
      elements.push(
        <div key={`p-${elements.length}`} style={{ marginTop: 2, lineHeight: 1.45 }}>
          {trimmed}
        </div>
      );
    }
  }
  flushBullets();
  return elements;
};

// OperatorCallLogItem component for displaying individual operator call entries
const OperatorCallLogItem = ({ log, customerId }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryText, setSummaryText] = useState(log.conversation_summary || '');
  const [summaryNotes, setSummaryNotes] = useState(log.conversation_transcript || '');
  const [summaryError, setSummaryError] = useState('');

  const formatDateTime = (isoString) => {
    if (!isoString) return 'N/A';
    try {
      const date = new Date(isoString);
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return isoString;
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0s';
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const getStatusInfo = (status) => {
    const map = {
      completed: { label: 'Accepted', color: '#16a34a', bg: '#dcfce7' },
      connected: { label: 'Connected', color: '#0284c7', bg: '#e0f2fe' },
      initiated: { label: 'Initiated', color: '#d97706', bg: '#fef3c7' },
      dialing: { label: 'Dialing', color: '#d97706', bg: '#fef3c7' },
      failed: { label: 'Failed', color: '#dc2626', bg: '#fee2e2' },
      rejected: { label: 'Rejected', color: '#dc2626', bg: '#fee2e2' },
      no_answer: { label: 'No Answer', color: '#dc2626', bg: '#fee2e2' },
    };
    return map[status] || { label: status || 'Unknown', color: '#6b7280', bg: '#f3f4f6' };
  };

  const getCallTypeInfo = (callType) => {
    if (callType === 'incoming') {
      return { label: 'Incoming', icon: 'fa-phone', color: '#0284c7', bg: '#e0f2fe' };
    }
    return { label: 'Outgoing', icon: 'fa-phone', color: '#7c3aed', bg: '#ede9fe' };
  };

  const getSourceLabel = (source) => {
    if (source === 'phone_sync') return 'Phone Sync';
    return 'Dashboard';
  };

  const handleSummarizeConversation = async (force = false) => {
    if (!log?.id || !customerId) return;
    if (!log.recording_url) {
      setSummaryError('No recording available for this call.');
      return;
    }
    setSummaryLoading(true);
    setSummaryError('');
    try {
      const response = await axios.post(
        `${config.customerApiBase}/customers/${customerId}/call-logs/${log.id}/summarize`,
        { force },
        { timeout: 180000 }
      );
      const summary = response.data?.summary || '';
      const notes = response.data?.transcript || '';
      setSummaryText(summary);
      setSummaryNotes(notes);
    } catch (error) {
      console.error('Failed to summarize call:', error);
      const detail = error.response?.data?.detail || error.message;
      setSummaryError(detail);
    } finally {
      setSummaryLoading(false);
    }
  };

  const statusInfo = getStatusInfo(log.status);
  const callTypeInfo = getCallTypeInfo(log.call_type);
  const phone = log.customer_phone;

  return (
    <li className={styles.operatorCallItem}>
      <div
        className={styles.operatorCallHeader}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className={styles.operatorCallMain}>
          <span
            className={styles.operatorCallStatus}
            style={{ color: statusInfo.color, background: statusInfo.bg }}
          >
            {statusInfo.label}
          </span>
          <span
            className={styles.operatorCallTypeBadge}
            style={{ color: callTypeInfo.color, background: callTypeInfo.bg }}
          >
            <i className={`fa ${callTypeInfo.icon}`} />
            {callTypeInfo.label}
          </span>
          {log.sim_number && (
            <span className={styles.operatorCallSim}>SIM: {log.sim_number}</span>
          )}
          {log.has_conversation_summary && (
            <span
              style={{ color: '#16a34a', fontSize: '0.7rem', fontWeight: 600 }}
              title="Summary available"
            >
              <i className="fa fa-check-circle" /> Summary
            </span>
          )}
        </div>
        <div className={styles.operatorCallMeta}>
          {log.recording_url && (
            <span className={styles.operatorCallRecording} title="Recording available">
              <i className="fa fa-microphone" />
            </span>
          )}
          <span className={styles.operatorCallDuration}>
            <i className="fa fa-clock-o" /> {formatDuration(log.duration_seconds)}
          </span>
          <span className={styles.operatorCallDate}>
            {formatDateTime(log.started_at || log.created_at)}
          </span>
          <i className={`fa fa-chevron-${isExpanded ? 'up' : 'down'} ${styles.operatorCallExpand}`} />
        </div>
      </div>
      {isExpanded && (
        <div className={styles.operatorCallDetails}>
          <table className={styles.infoTable}>
            <tbody>
              <InfoRow label="Call ID" value={`#${log.id}`} />
              <InfoRow
                label="Direction"
                renderValue={() => (
                  <span style={{ color: callTypeInfo.color, fontWeight: 600 }}>
                    <i className={`fa ${callTypeInfo.icon}`} style={{ marginRight: '0.35rem' }} />
                    {callTypeInfo.label}
                  </span>
                )}
              />
              {phone && <InfoRow label="Phone" value={phone} />}
              {log.operator_id && <InfoRow label="Operator" value={`User #${log.operator_id}`} />}
              {log.sim_number && <InfoRow label="SIM Number" value={log.sim_number} />}
              {log.sim_role && <InfoRow label="SIM Role" value={log.sim_role} />}
              <InfoRow label="Source" value={getSourceLabel(log.source)} />
              <InfoRow label="Status" renderValue={() => (
                <span style={{ color: statusInfo.color, fontWeight: 600 }}>{statusInfo.label}</span>
              )} />
              <InfoRow label="Duration" value={formatDuration(log.duration_seconds)} />
              <InfoRow label="Started" value={formatDateTime(log.started_at)} />
              <InfoRow label="Ended" value={formatDateTime(log.ended_at)} />
              <InfoRow label="Created" value={formatDateTime(log.created_at)} />
            </tbody>
          </table>
          {log.recording_url && (
            <div className={styles.operatorCallRecordingSection}>
              <div className={styles.recordingLabel}>
                <i className="fa fa-headphones" /> Call Recording
              </div>
              <audio
                controls
                src={log.recording_url}
                className={styles.recordingPlayer}
              >
                Your browser does not support the audio element.
              </audio>
            </div>
          )}
          {log.recording_url && (
            <div style={{ marginTop: '0.75rem' }}>
              <button
                style={{
                  background: summaryLoading ? '#94a3b8' : '#2563eb',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  padding: '6px 14px',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  cursor: summaryLoading ? 'not-allowed' : 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
                disabled={summaryLoading}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSummarizeConversation(false);
                }}
              >
                {summaryLoading ? (
                  <>
                    <Spinner size="sm" /> Listening to recording & summarizing...
                  </>
                ) : (
                  <>
                    <i className="fa fa-align-left" /> {summaryText ? 'Regenerate Summary' : 'Summarize Conversation'}
                  </>
                )}
              </button>
            </div>
          )}
          {summaryError && (
            <div style={{ color: '#dc2626', fontSize: '0.8rem', marginTop: '0.5rem' }}>
              {summaryError}
            </div>
          )}
          {summaryText && (
            <div
              style={{
                marginTop: '0.75rem',
                padding: '0.75rem',
                background: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                fontSize: '0.85rem',
                lineHeight: 1.5,
                color: '#1e293b',
              }}
            >
              <div style={{ fontSize: '0.7rem', color: '#64748b', marginBottom: 4, fontWeight: 600 }}>
                Summary
              </div>
              {summaryText}
            </div>
          )}
          {summaryNotes && summaryNotes !== summaryText && (
            <div
              style={{
                marginTop: '0.5rem',
                padding: '0.75rem',
                background: '#fff',
                border: '1px solid #e2e8f0',
                borderRadius: 8,
                fontSize: '0.78rem',
                lineHeight: 1.45,
                color: '#334155',
                maxHeight: 280,
                overflowY: 'auto',
              }}
            >
              <div style={{ fontSize: '0.7rem', color: '#64748b', marginBottom: 4, fontWeight: 600 }}>
                Ops / Customer notes (from audio)
              </div>
              {renderMarkdownNotes(summaryNotes)}
            </div>
          )}
        </div>
      )}
    </li>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const CustomerDetailsModal = ({
  isOpen,
  toggle,
  customerId,
  onCustomerUpdated,
  onViewAllCalls,
  activityRefreshKey = 0,
  stackAboveOverlay = false,
}) => {
  const { isDateInRange } = useDateFilter();
  const { openChat } = useChat();
  const [customerDetails, setCustomerDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [conversationsTotal, setConversationsTotal] = useState(0);
  const [conversationsLoading, setConversationsLoading] = useState(false);
  const [conversationsError, setConversationsError] = useState(null);
  const [showAllBookings, setShowAllBookings] = useState(false);
  const [taxonomyData, setTaxonomyData] = useState({});
  const history = useHistory();

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: '',
    phone: '',
    business_name: '',
    preferred_language: '',
    business_category: '',
    matched_category: '',
    domain: '',
    remarks: '',
    default_material_description: '',
    discount_percentage: '',
    gstin: '',
    primary_address_id: null,
    star: false,
  });
  const [saving, setSaving] = useState(false);
  const [blocking, setBlocking] = useState(false);
  const [autoReplyFlagging, setAutoReplyFlagging] = useState(false);
  const [starring, setStarring] = useState(false);
  const [marketingOptingOut, setMarketingOptingOut] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [useCustomLanguageInput, setUseCustomLanguageInput] = useState(false);
  const [isRemarksEditing, setIsRemarksEditing] = useState(false);
  const [remarksDraft, setRemarksDraft] = useState('');
  const [remarksSaving, setRemarksSaving] = useState(false);
  const [deletingSummary, setDeletingSummary] = useState(false);
  const [showAllSummaries, setShowAllSummaries] = useState(false);
  const [activeCallTab, setActiveCallTab] = useState('bot');
  const [voiceMktSummaries, setVoiceMktSummaries] = useState([]);
  const [voiceMktLoading, setVoiceMktLoading] = useState(false);
  const [voiceMktError, setVoiceMktError] = useState('');
  const [voiceMktHasMore, setVoiceMktHasMore] = useState(false);
  const [voiceMktTotal, setVoiceMktTotal] = useState(0);
  const [operatorCallLogs, setOperatorCallLogs] = useState([]);
  const [operatorCallLogsTotal, setOperatorCallLogsTotal] = useState(0);
  const [operatorCallLogsLoading, setOperatorCallLogsLoading] = useState(false);
  const [operatorCallLogsError, setOperatorCallLogsError] = useState(null);
  const [extraPhones, setExtraPhones] = useState([]);
  const [extraForm, setExtraForm] = useState({ phone: '', label: '', remark: '' });
  const [extraWarning, setExtraWarning] = useState(null);
  const [extraError, setExtraError] = useState('');
  const [extraSaving, setExtraSaving] = useState(false);
  const [editingExtraId, setEditingExtraId] = useState(null);
  const [extraPhonesOpen, setExtraPhonesOpen] = useState(false);

  // Active leads state
  const [activeLeads, setActiveLeads] = useState([]);
  const [activeLeadsTotal, setActiveLeadsTotal] = useState(0);
  const [activeLeadsLoading, setActiveLeadsLoading] = useState(false);
  const [activeLeadsError, setActiveLeadsError] = useState(null);

  // Address search state
  const [addressInput, setAddressInput] = useState('');
  const [addressLoading, setAddressLoading] = useState(false);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [addressSelection, setAddressSelection] = useState(null);

  const saveExtraPhone = async (confirm = false) => {
    if (!customerId) return;
    setExtraSaving(true);
    setExtraError('');
    if (!confirm) setExtraWarning(null);
    try {
      const response = await fetch(`${config.customerApiBase}/customers/${customerId}/phones`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          phone_number: extraForm.phone,
          label: extraForm.label,
          remark: extraForm.remark,
          confirm,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (response.status === 409 && data.detail?.needs_confirm) {
        setExtraWarning(data.detail);
        return;
      }
      if (!response.ok) {
        const detail = data.detail;
        throw new Error(typeof detail === 'string' ? detail : (detail?.message || 'Failed to save number'));
      }
      setExtraPhones((prev) => [...prev, data.phone]);
      setExtraForm({ phone: '', label: '', remark: '' });
      setExtraWarning(null);
    } catch (err) {
      setExtraError(err.message || 'Failed to save number');
    } finally {
      setExtraSaving(false);
    }
  };

  const updateExtraPhone = async (entry) => {
    setExtraSaving(true);
    setExtraError('');
    try {
      const response = await fetch(
        `${config.customerApiBase}/customers/${customerId}/phones/${entry.id}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ label: entry.label, remark: entry.remark }),
        },
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(typeof data.detail === 'string' ? data.detail : 'Failed to update number');
      }
      setExtraPhones((prev) => prev.map((item) => (item.id === data.phone.id ? data.phone : item)));
      setEditingExtraId(null);
    } catch (err) {
      setExtraError(err.message || 'Failed to update number');
    } finally {
      setExtraSaving(false);
    }
  };

  const deleteExtraPhone = async (phoneId) => {
    setExtraSaving(true);
    setExtraError('');
    try {
      const response = await fetch(
        `${config.customerApiBase}/customers/${customerId}/phones/${phoneId}`,
        { method: 'DELETE' },
      );
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(typeof data.detail === 'string' ? data.detail : 'Failed to remove number');
      }
      setExtraPhones((prev) => prev.filter((item) => item.id !== phoneId));
    } catch (err) {
      setExtraError(err.message || 'Failed to remove number');
    } finally {
      setExtraSaving(false);
    }
  };

  const resetAddressEditState = () => {
    setAddressInput('');
    setAddressSuggestions([]);
    setAddressSelection(null);
    setAddressLoading(false);
  };

  useEffect(() => {
    if (isOpen) {
      fetch(`${config.customerApiBase}/taxonomy`)
        .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`taxonomy ${res.status}`))))
        .then((data) => setTaxonomyData(normalizeCustomerTaxonomyResponse(data)))
        .catch((err) => {
          console.error('Failed to fetch taxonomy:', err);
          setTaxonomyData({});
        });
    }
  }, [isOpen]);

  const fetchCustomerDetails = async () => {
    if (!customerId) return;

    try {
      setLoading(true);
      setError(null);
      setCustomerDetails(null);
      const response = await fetch(
        `${config.customerApiBase}/customers/${customerId}/details`
      );

      if (!response.ok) {
        throw new Error(`Server responded with status ${response.status}`);
      }

      const data = await response.json();
      setCustomerDetails(data);
      setExtraPhones(Array.isArray(data.customer?.extra_phones) ? data.customer.extra_phones : []);
      setExtraForm({ phone: '', label: '', remark: '' });
      setExtraWarning(null);
      setExtraError('');
      setExtraPhonesOpen(false);

      // Initialize edit form with current data
      setEditFormData({
        name: data.customer?.name || '',
        phone: data.customer?.phone || '',
        business_name: data.customer?.business_name || '',
        preferred_language: data.customer?.preferred_language || '',
        business_category: data.customer?.business_category || '',
        matched_category: data.customer?.matched_category || '',
        domain: data.customer?.domain || '',
        remarks: data.customer?.remarks || '',
        default_material_description: data.customer?.default_material_description || '',
        discount_percentage: data.customer?.discount_percentage ?? '',
        gstin: data.customer?.gstin || '',
        primary_address_id: data.customer?.primary_address_id || null,
        star: !!data.customer?.starred,
      });
      setRemarksDraft(data.customer?.remarks || '');
      setUseCustomLanguageInput(
        !!data.customer?.preferred_language && !isPreferredLanguageOption(data.customer?.preferred_language)
      );
    } catch (err) {
      console.error('Error fetching customer details:', err);
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleEditToggle = () => {
    if (isEditing) {
      // Reset form data if canceling edit
      setEditFormData({
        name: customerDetails.customer?.name || '',
        phone: customerDetails.customer?.phone || '',
        business_name: customerDetails.customer?.business_name || '',
        preferred_language: customerDetails.customer?.preferred_language || '',
        business_category: customerDetails.customer?.business_category || '',
        matched_category: customerDetails.customer?.matched_category || '',
        domain: customerDetails.customer?.domain || '',
        remarks: customerDetails.customer?.remarks || '',
        default_material_description: customerDetails.customer?.default_material_description || '',
        discount_percentage: customerDetails.customer?.discount_percentage ?? '',
        gstin: customerDetails.customer?.gstin || '',
        primary_address_id: customerDetails.customer?.primary_address_id || null,
        star: !!customerDetails.customer?.starred,
      });
      setUseCustomLanguageInput(
        !!customerDetails.customer?.preferred_language &&
        !isPreferredLanguageOption(customerDetails.customer?.preferred_language)
      );
      setSaveError(null);
      resetAddressEditState();
    }
    setIsRemarksEditing(false);
    setIsEditing(!isEditing);
  };

  const handleOpenRemarksEditor = () => {
    if (isEditing) return;
    setSaveError(null);
    setRemarksDraft(customerDetails?.customer?.remarks || '');
    setIsRemarksEditing(true);
  };

  const handleCancelRemarksEdit = () => {
    setRemarksDraft(customerDetails?.customer?.remarks || '');
    setIsRemarksEditing(false);
  };

  const handleSaveRemarks = async () => {
    if (!customerId) return;
    try {
      setRemarksSaving(true);
      setSaveError(null);

      const response = await fetch(
        `${config.customerApiBase}/customers/${customerId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ remarks: remarksDraft })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Server responded with status ${response.status}`);
      }

      await fetchCustomerDetails();
      setIsRemarksEditing(false);
      if (onCustomerUpdated) {
        onCustomerUpdated();
      }
    } catch (err) {
      console.error('Error saving remarks:', err);
      setSaveError(err.message || 'Failed to save remarks');
    } finally {
      setRemarksSaving(false);
    }
  };

  const handleResolveAddress = async () => {
    if (!addressInput.trim()) {
      setSaveError('Please enter an address to search');
      return;
    }

    setAddressLoading(true);
    setAddressSuggestions([]);
    setSaveError(null);

    try {
      const response = await fetch(`${config.customerApiBase}/resolve-location`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ address_text: addressInput.trim() }),
      });

      if (!response.ok) {
        throw new Error('Failed to resolve location');
      }

      const data = await response.json();

      if (data.type === 'similar' && Array.isArray(data.matches)) {
        setAddressSelection(null);
        setAddressSuggestions(data.matches);
        return;
      }

      // Exact match or new address
      setAddressSuggestions([]);
      setAddressSelection({
        id: data.address_id,
        text: data.resolved_address,
        lat: data.latitude,
        lng: data.longitude,
      });
      setEditFormData({ ...editFormData, primary_address_id: data.address_id });
    } catch (err) {
      setSaveError(`Failed to resolve address: ${err.message}`);
      setAddressSelection(null);
    } finally {
      setAddressLoading(false);
    }
  };

  const handleAddressSuggestionSelect = async (suggestion) => {
    setAddressLoading(true);
    try {
      setSaveError(null);

      let resolvedId = suggestion?.id ?? suggestion?.address_id ?? null;
      let resolvedText = suggestion?.locality || suggestion?.resolved_address || '';
      let resolvedLat = suggestion?.latitude;
      let resolvedLng = suggestion?.longitude;

      // Google-place suggestions may not carry an address ID.
      // Resolve by coordinates to get canonical address_id from backend.
      if (!resolvedId && resolvedLat != null && resolvedLng != null) {
        const response = await fetch(`${config.customerApiBase}/resolve-location`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            lat: Number(resolvedLat),
            lng: Number(resolvedLng),
          }),
        });

        if (!response.ok) {
          throw new Error('Failed to resolve selected suggestion');
        }

        const data = await response.json();
        if (!data?.address_id) {
          throw new Error('Selected suggestion did not return a valid address ID');
        }

        resolvedId = data.address_id;
        resolvedText = data.resolved_address || resolvedText;
        resolvedLat = data.latitude ?? resolvedLat;
        resolvedLng = data.longitude ?? resolvedLng;
      }

      if (!resolvedId) {
        throw new Error('Address ID is missing for selected suggestion');
      }

      setAddressSelection({
        id: resolvedId,
        text: resolvedText,
        lat: resolvedLat,
        lng: resolvedLng,
      });
      setAddressSuggestions([]);
      setAddressInput(resolvedText || '');
      setEditFormData((prev) => ({ ...prev, primary_address_id: Number(resolvedId) }));
    } catch (err) {
      setSaveError(`Failed to select address: ${err.message}`);
      setAddressSelection(null);
    } finally {
      setAddressLoading(false);
    }
  };

  const handleDeleteCallSummary = async (entryToDelete) => {
    setDeletingSummary(true);

    const allEntries = parseVoiceCallEntries(customerDetails.customer?.call_summary);
    const deleteIndex = allEntries.findIndex(e => e.timestamp === entryToDelete.timestamp && e.raw === entryToDelete.raw && e.body === entryToDelete.body);
    if (deleteIndex === -1) { setDeletingSummary(false); return; }

    const updatedEntries = allEntries.filter((_, idx) => idx !== deleteIndex);
    const updatedSummaryString = updatedEntries.map(e => e.text).join('\n\n');

    try {
      const response = await fetch(`${config.customerApiBase}/customers/${customerId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ call_summary: updatedSummaryString })
      });
      if (!response.ok) throw new Error('Failed to delete call summary');
      await fetchCustomerDetails();
    } catch (err) {
      console.error('Error deleting summary:', err);
      setSaveError('Failed to delete call summary entry.');
    } finally {
      setDeletingSummary(false);
    }
  };

  const handleSaveChanges = async () => {
    try {
      setSaving(true);
      setSaveError(null);

      const payload = {
        ...editFormData,
        discount_percentage:
          editFormData.discount_percentage === '' || editFormData.discount_percentage === null
            ? null
            : Number(editFormData.discount_percentage),
      };

      const response = await fetch(
        `${config.customerApiBase}/customers/${customerId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || `Server responded with status ${response.status}`);
      }

      // Refresh customer details after save
      await fetchCustomerDetails();
      setIsEditing(false);
    } catch (err) {
      console.error('Error saving customer details:', err);
      setSaveError(err.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleStarToggle = async () => {
    if (!customerDetails?.customer?.id) return;

    const currentlyStarred = !!customerDetails.customer.starred;
    const nextStarred = !currentlyStarred;

    try {
      setStarring(true);
      setSaveError(null);

      const response = await fetch(
        `${config.customerApiBase}/customers/${customerDetails.customer.id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ star: nextStarred })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to update important status');
      }

      await fetchCustomerDetails();
      if (onCustomerUpdated) {
        onCustomerUpdated();
      }
    } catch (err) {
      setSaveError(err.message || 'Failed to update important status');
    } finally {
      setStarring(false);
    }
  };

  const handleAutoReplyFlagToggle = async () => {
    if (!customerDetails?.customer?.id) return;

    const currentlyFlagged = !!customerDetails.customer.is_auto_reply_flagged;
    const nextFlagged = !currentlyFlagged;

    try {
      setAutoReplyFlagging(true);
      setSaveError(null);

      const response = await fetch(
        `${config.customerApiBase}/customers/${customerDetails.customer.id}/auto-reply-flag`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ is_auto_reply_flagged: nextFlagged })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to update auto-reply flag');
      }

      await fetchCustomerDetails();
      if (onCustomerUpdated) {
        onCustomerUpdated();
      }
    } catch (err) {
      setSaveError(err.message || 'Failed to update auto-reply flag');
    } finally {
      setAutoReplyFlagging(false);
    }
  };

  const handleMarketingOptOutToggle = async () => {
    if (!customerDetails?.customer?.id) return;

    const currentlyOptedOut = !!customerDetails.customer.marketing_opt_out;
    const nextOptedOut = !currentlyOptedOut;

    try {
      setMarketingOptingOut(true);
      setSaveError(null);

      const response = await fetch(
        `${config.customerApiBase}/customers/${customerDetails.customer.id}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ marketing_opt_out: nextOptedOut })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to update marketing opt-out status');
      }

      await fetchCustomerDetails();
      if (onCustomerUpdated) {
        onCustomerUpdated();
      }
    } catch (err) {
      setSaveError(err.message || 'Failed to update marketing opt-out status');
    } finally {
      setMarketingOptingOut(false);
    }
  };

  const handleBlockToggle = async () => {
    if (!customerDetails?.customer?.id) return;

    const currentlyBlocked = !!customerDetails.customer.is_blocked;
    const nextBlocked = !currentlyBlocked;

    if (!nextBlocked) {
      const confirmed = window.confirm(
        'This will unblock the user and end the current conversation session. Continue?'
      );
      if (!confirmed) return;
    }

    try {
      setBlocking(true);
      setSaveError(null);

      const response = await fetch(
        `${config.customerApiBase}/customers/${customerDetails.customer.id}/block`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ is_blocked: nextBlocked })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to update block status');
      }

      await fetchCustomerDetails();
      if (onCustomerUpdated) {
        onCustomerUpdated();
      }
    } catch (err) {
      setSaveError(err.message || 'Failed to update block status');
    } finally {
      setBlocking(false);
    }
  };

  const fetchConversations = async () => {
    if (!customerId) return;

    try {
      setConversationsLoading(true);
      setConversationsError(null);
      const response = await fetch(
        `${config.customerApiBase}/customers/${customerId}/conversations?limit=5&offset=0`
      );

      if (!response.ok) {
        throw new Error(`Server responded with status ${response.status}`);
      }

      const data = await response.json();
      setConversations(data.conversations || []);
      setConversationsTotal(
        data.conversations_total != null
          ? data.conversations_total
          : (data.conversations || []).length
      );
    } catch (err) {
      console.error('Error fetching conversations:', err);
      setConversationsError(err.message || 'An unexpected error occurred');
    } finally {
      setConversationsLoading(false);
    }
  };

  const loadAllConversations = async () => {
    const remaining = conversationsTotal - conversations.length;
    if (!customerId || remaining <= 0) return;
    try {
      setConversationsLoading(true);
      setConversationsError(null);
      const response = await fetch(
        `${config.customerApiBase}/customers/${customerId}/conversations?limit=${remaining}&offset=${conversations.length}`
      );
      if (!response.ok) {
        throw new Error(`Server responded with status ${response.status}`);
      }
      const data = await response.json();
      setConversations((prev) => [...prev, ...(data.conversations || [])]);
    } catch (err) {
      console.error('Error loading all conversations:', err);
      setConversationsError(err.message || 'Failed to load conversations');
    } finally {
      setConversationsLoading(false);
    }
  };

  const fetchOperatorCallLogs = async () => {
    if (!customerId) return;
    try {
      setOperatorCallLogsLoading(true);
      setOperatorCallLogsError(null);
      const response = await fetch(
        `${config.customerApiBase}/customers/${customerId}/call-logs?limit=5`
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch call logs: ${response.status}`);
      }
      const data = await response.json();
      const logs = (data.data || []).filter((log) => {
        if (log.entry_kind === 'ops_event') return false;
        if (String(log.source || '').toLowerCase() === 'segment_flow') return false;
        if (log.action_type && !log.status && !log.sim_number) return false;
        return true;
      });
      setOperatorCallLogs(logs);
      // Always trust the backend total (true count of operator calls for this customer),
      // not the length of the first page of filtered rows — the fetch is capped at
      // limit=5, so logs.length is at most 5 and must never be used as the total.
      setOperatorCallLogsTotal(data.total || 0);
    } catch (err) {
      console.error('Error fetching operator call logs:', err);
      setOperatorCallLogsError(err.message || 'Failed to load call logs');
    } finally {
      setOperatorCallLogsLoading(false);
    }
  };

  const fetchActiveLeads = async () => {
    if (!customerId) return;
    try {
      setActiveLeadsLoading(true);
      setActiveLeadsError(null);
      const response = await fetch(
        `${config.customerApiBase}/booking-leads?customer_id=${customerId}&limit=5&sort_by=created_at&sort_order=desc&exclude_status=dropped`
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch active leads: ${response.status}`);
      }
      const data = await response.json();
      const active = (data.leads || []).filter((lead) => lead.status !== 'dropped');
      setActiveLeads(active);
      setActiveLeadsTotal(data.total || 0);
    } catch (err) {
      console.error('Error fetching active leads:', err);
      setActiveLeadsError(err.message || 'Failed to load active leads');
    } finally {
      setActiveLeadsLoading(false);
    }
  };

  const loadAllLeads = async () => {
    const remaining = activeLeadsTotal - activeLeads.length;
    if (!customerId || remaining <= 0) return;
    try {
      setActiveLeadsLoading(true);
      setActiveLeadsError(null);
      const response = await fetch(
        `${config.customerApiBase}/booking-leads?customer_id=${customerId}&limit=${remaining}&offset=${activeLeads.length}&sort_by=created_at&sort_order=desc&exclude_status=dropped`
      );
      if (!response.ok) {
        throw new Error(`Failed to fetch active leads: ${response.status}`);
      }
      const data = await response.json();
      const more = (data.leads || []).filter((lead) => lead.status !== 'dropped');
      setActiveLeads((prev) => [...prev, ...more]);
    } catch (err) {
      console.error('Error loading all leads:', err);
      setActiveLeadsError(err.message || 'Failed to load all leads');
    } finally {
      setActiveLeadsLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setIsEditing(false);
      setIsRemarksEditing(false);
      setSaveError(null);
      setShowAllBookings(false);
      resetAddressEditState();
      setActiveCallTab('bot');
      setOperatorCallLogs([]);
      setOperatorCallLogsTotal(0);
      setOperatorCallLogsError(null);
      setActiveLeads([]);
      setActiveLeadsTotal(0);
      setActiveLeadsError(null);
      setConversationsTotal(0);
      setVoiceMktSummaries([]);
      setVoiceMktError('');
      setVoiceMktHasMore(false);
      setVoiceMktTotal(0);
      setShowAllSummaries(false);
      return;
    }

    if (customerId) {
      setIsEditing(false);
      setIsRemarksEditing(false);
      setSaveError(null);
      setShowAllBookings(false);
      resetAddressEditState();
      setActiveCallTab('bot');
      fetchCustomerDetails();
      fetchConversations();
      fetchOperatorCallLogs();
      fetchActiveLeads();
    }
  }, [isOpen, customerId]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const phone = customerDetails?.customer?.phone;
    const nwPhone = customerDetails?.customer?.non_whatsapp_phone;
    if (!phone && !nwPhone) return undefined;
    let cancelled = false;
    const load = async () => {
      setVoiceMktLoading(true);
      setVoiceMktError('');
      try {
        const limit = showAllSummaries ? 50 : 5;
        const loads = [];
        if (phone) {
          loads.push(fetchVoiceMktCustomerHistory(phone, {
            summaryOffset: 0,
            summaryLimit: limit,
          }));
        }
        if (nwPhone && String(nwPhone).replace(/\D/g, '').slice(-10)
          !== String(phone || '').replace(/\D/g, '').slice(-10)) {
          loads.push(fetchVoiceMktCustomerHistory(nwPhone, {
            summaryOffset: 0,
            summaryLimit: limit,
          }));
        }
        const results = await Promise.all(loads);
        if (cancelled) return;
        const merged = [];
        const seen = new Set();
        for (const data of results) {
          for (const row of (Array.isArray(data?.summaries) ? data.summaries : [])) {
            const key = row?.id ?? row?.call_log_id ?? JSON.stringify(row);
            if (seen.has(key)) continue;
            seen.add(key);
            merged.push(row);
          }
        }
        merged.sort((a, b) => String(b?.started_at || b?.created_at || '')
          .localeCompare(String(a?.started_at || a?.created_at || '')));
        const trimmed = merged.slice(0, limit);
        setVoiceMktSummaries(trimmed);
        setVoiceMktHasMore(merged.length > limit);
        setVoiceMktTotal(merged.length);
      } catch (err) {
        if (cancelled) return;
        setVoiceMktSummaries([]);
        setVoiceMktError(err.message || 'Could not load voice tracker summaries');
      } finally {
        if (!cancelled) setVoiceMktLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [isOpen, customerDetails?.customer?.phone, customerDetails?.customer?.non_whatsapp_phone, showAllSummaries]);

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleOpenLead = (leadId) => {
    toggle();
    history.push(`/app/leads?expandLead=${leadId}`);
  };

  const whatsAppTimeline = useMemo(() => {
    return conversations
      .map((c) => ({ kind: 'conversation', ts: c.created_at || c.timestamp, data: c }))
      .filter((item) => !item.ts || isDateInRange(item.ts))
      .sort((a, b) => new Date(b.ts || 0) - new Date(a.ts || 0));
  }, [conversations, isDateInRange]);

  const additionalNumbers = (
    <div className={styles.extraPhonesSlot}>
      <button
        type="button"
        className={styles.extraPhonesToggle}
        onClick={() => setExtraPhonesOpen((open) => !open)}
      >
        <i className={`fa fa-${extraPhonesOpen ? 'chevron-up' : 'plus'}`} />
        {extraPhonesOpen
          ? 'Hide additional numbers'
          : (extraPhones.length
            ? `Additional numbers (${extraPhones.length})`
            : 'Add additional number')}
      </button>
      {extraPhonesOpen && (
        <div className={styles.extraPhones}>
          {extraPhones.length > 0 && (
            <div className={styles.extraPhoneList}>
              {extraPhones.map((entry) => (
                <div key={entry.id} className={styles.extraPhoneRow}>
                  <span className={styles.extraPhoneRowIcon} aria-hidden="true">
                    <i className="fa fa-phone" />
                  </span>
                  <div className={styles.extraPhoneMain}>
                    {editingExtraId === entry.id ? (
                      <div className={styles.extraPhoneEdit}>
                        <label className={styles.extraPhoneField}>
                          <span>Contact name</span>
                          <input
                            value={entry.label || ''}
                            onChange={(e) => setExtraPhones((prev) => prev.map((item) => (
                              item.id === entry.id ? { ...item, label: e.target.value } : item
                            )))}
                          />
                        </label>
                        <label className={styles.extraPhoneField}>
                          <span>Remark</span>
                          <input
                            value={entry.remark || ''}
                            onChange={(e) => setExtraPhones((prev) => prev.map((item) => (
                              item.id === entry.id ? { ...item, remark: e.target.value } : item
                            )))}
                          />
                        </label>
                      </div>
                    ) : (
                      <>
                        <div className={styles.extraPhoneIdentity}>
                          <strong>{entry.phone}</strong>
                          {entry.label && <span className={styles.extraPhoneLabel}>{entry.label}</span>}
                        </div>
                        {entry.remark && <span className={styles.extraPhoneMeta}>{entry.remark}</span>}
                      </>
                    )}
                  </div>
                  <div className={styles.extraPhoneActions}>
                    {editingExtraId === entry.id ? (
                      <button
                        type="button"
                        className={styles.extraPhoneSave}
                        onClick={() => updateExtraPhone(entry)}
                        disabled={extraSaving}
                      >
                        <i className={`fa ${extraSaving ? 'fa-spinner fa-spin' : 'fa-check'}`} />
                        Save
                      </button>
                    ) : (
                      <button
                        type="button"
                        className={styles.extraPhoneEditBtn}
                        onClick={() => setEditingExtraId(entry.id)}
                        disabled={extraSaving}
                        aria-label={`Edit ${entry.phone}`}
                        title="Edit contact details"
                      >
                        <i className="fa fa-pencil" />
                      </button>
                    )}
                    <button
                      type="button"
                      className={styles.extraPhoneRemove}
                      onClick={() => deleteExtraPhone(entry.id)}
                      disabled={extraSaving}
                      aria-label={`Remove ${entry.phone}`}
                      title="Remove number"
                    >
                      <i className={`fa ${extraSaving ? 'fa-spinner fa-spin' : 'fa-trash-o'}`} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className={styles.extraPhoneAddPanel}>
            <div className={styles.extraPhoneAddHeading}>
              <span>Add a number</span>
            </div>
            <div className={styles.extraPhoneAdd}>
              <label className={`${styles.extraPhoneField} ${styles.extraPhoneNumberField}`}>
                <span>Phone number</span>
                <input
                  type="tel"
                  autoComplete="tel"
                  placeholder="e.g. +91 98765 43210"
                  value={extraForm.phone}
                  onChange={(e) => setExtraForm({ ...extraForm, phone: e.target.value })}
                />
              </label>
              <label className={styles.extraPhoneField}>
                <span>Name</span>
                <input
                  placeholder="e.g. Accounts"
                  value={extraForm.label}
                  onChange={(e) => setExtraForm({ ...extraForm, label: e.target.value })}
                />
              </label>
              <label className={styles.extraPhoneField}>
                <span>Remark</span>
                <input
                  placeholder="e.g. Call after 2 pm"
                  value={extraForm.remark}
                  onChange={(e) => setExtraForm({ ...extraForm, remark: e.target.value })}
                />
              </label>
              <button
                type="button"
                className={styles.extraPhoneAddBtn}
                onClick={() => saveExtraPhone(false)}
                disabled={extraSaving || !extraForm.phone.trim()}
              >
                <i className={`fa ${extraSaving ? 'fa-spinner fa-spin' : 'fa-plus'}`} />
                Add number
              </button>
            </div>
          </div>
          {extraWarning?.needs_confirm && (
            <div className={styles.extraPhoneWarning} role="alert">
              <div className={styles.extraPhoneWarningTitle}>
                <i className="fa fa-exclamation-triangle" />
                <span>{extraWarning.message}</span>
              </div>
              {extraWarning.conflicts?.length > 0 && (
                <ul>
                  {extraWarning.conflicts.map((conflict) => (
                    <li key={`${conflict.customer_id}-${conflict.source}`}>
                      {conflict.name} <span>Customer {conflict.customer_id} · {conflict.source === 'main' ? 'primary number' : 'other number'}</span>
                    </li>
                  ))}
                </ul>
              )}
              <button type="button" onClick={() => saveExtraPhone(true)} disabled={extraSaving}>
                <i className="fa fa-check" />
                Save anyway
              </button>
            </div>
          )}
          {extraError && <div className={styles.extraPhoneError} role="alert"><i className="fa fa-exclamation-circle" />{extraError}</div>}
        </div>
      )}
    </div>
  );

  return (
    <>
      <Modal
        isOpen={isOpen}
        toggle={toggle}
        size="lg"
        scrollable
        zIndex={stackAboveOverlay ? 1350 : undefined}
      >
        <div className={styles.modalHeader}>
          <div className={styles.headerIcon}>
            <i className="fa fa-user-circle"></i>
          </div>
          <h5 className={styles.headerTitle}>Customer Details</h5>
          {!loading && !error && customerDetails && (
            <>
              {isEditing ? (
                <>
                  <button
                    onClick={handleSaveChanges}
                    disabled={saving}
                    className={styles.saveBtn}
                    style={{ marginLeft: 'auto', marginRight: '0.5rem' }}
                  >
                    <i className={`fa ${saving ? 'fa-spinner fa-spin' : 'fa-check'}`}></i>
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                  <button
                    onClick={handleEditToggle}
                    disabled={saving}
                    className={styles.cancelBtn}
                    style={{ marginRight: '0.5rem' }}
                  >
                    <i className="fa fa-times"></i> Cancel
                  </button>
                </>
              ) : (
                <button
                  onClick={handleEditToggle}
                  className={styles.editBtn}
                  style={{ marginLeft: 'auto', marginRight: '0.5rem' }}
                >
                  <i className="fa fa-pencil"></i> Edit
                </button>
              )}
            </>
          )}
          <button
            onClick={toggle}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.25rem', color: '#6c757d' }}
          >
            <i className="fa fa-times"></i>
          </button>
        </div>

        <ModalBody className={styles.modalBody}>
          {/* Loading State */}
          {loading && <LoadingState />}

          {/* Error State */}
          {error && <ErrorState error={error} onRetry={fetchCustomerDetails} />}

          {/* Content */}
          {!loading && !error && customerDetails && (
            <>
              {/* Save Error Message */}
              {saveError && (
                <div className={styles.errorBanner}>
                  <i className="fa fa-exclamation-circle"></i> {saveError}
                </div>
              )}

              {/* New Booking Button */}
              <div className={styles.actionBar}>
                <button
                  className={`${styles.starToggleBtn} ${customerDetails.customer?.starred ? styles.starred : styles.unstarred}`}
                  onClick={handleStarToggle}
                  disabled={starring}
                >
                  <i className={`fa ${starring ? 'fa-spinner fa-spin' : 'fa-star'}`}></i>
                  {starring
                    ? (customerDetails.customer?.starred ? 'Removing...' : 'Marking...')
                    : (customerDetails.customer?.starred ? 'Remove from Important' : 'Mark as Important')}
                </button>
                <button
                  className={`${styles.blockToggleBtn} ${customerDetails.customer?.is_blocked ? styles.unblock : styles.block}`}
                  onClick={handleBlockToggle}
                  disabled={blocking}
                >
                  <i className={`fa ${blocking ? 'fa-spinner fa-spin' : (customerDetails.customer?.is_blocked ? 'fa-unlock' : 'fa-lock')}`}></i>
                  {blocking
                    ? (customerDetails.customer?.is_blocked ? 'Unblocking...' : 'Blocking...')
                    : (customerDetails.customer?.is_blocked ? 'Unblock User' : 'Block User')}
                </button>
                <button
                  className={`${styles.autoReplyToggleBtn} ${customerDetails.customer?.is_auto_reply_flagged ? styles.autoReplyFlagged : styles.autoReplyClear}`}
                  onClick={handleAutoReplyFlagToggle}
                  disabled={autoReplyFlagging}
                  title="Mark customers whose WhatsApp messages are business auto-replies"
                >
                  <span className={styles.autoReplyBtnIcon} aria-hidden>
                    {autoReplyFlagging ? <i className="fa fa-spinner fa-spin" /> : '🤖'}
                  </span>
                  {autoReplyFlagging
                    ? (customerDetails.customer?.is_auto_reply_flagged ? 'Clearing...' : 'Flagging...')
                    : (customerDetails.customer?.is_auto_reply_flagged ? 'Clear Auto-Reply Flag' : 'Flag Auto-Reply')}
                </button>
                <button
                  className={`${styles.marketingOptOutBtn} ${customerDetails.customer?.marketing_opt_out ? styles.marketingOptedOut : styles.marketingOptedIn}`}
                  onClick={handleMarketingOptOutToggle}
                  disabled={marketingOptingOut}
                  title={customerDetails.customer?.marketing_opt_out
                    ? `Opted out${customerDetails.customer?.marketing_opted_out_at ? ` on ${new Date(customerDetails.customer.marketing_opted_out_at).toLocaleDateString()}` : ''} — click to opt in`
                    : 'Click to opt out of marketing calls'}
                >
                  <i className={`fa ${marketingOptingOut ? 'fa-spinner fa-spin' : (customerDetails.customer?.marketing_opt_out ? 'fa-ban' : 'fa-bullhorn')}`}></i>
                  {marketingOptingOut
                    ? (customerDetails.customer?.marketing_opt_out ? 'Opting in...' : 'Opting out...')
                    : (customerDetails.customer?.marketing_opt_out ? 'Marketing Opt-Out (Active)' : 'Marketing Opt-Out')}
                </button>
                <button
                  className={styles.newBookingBtn}
                  onClick={() => {
                    const params = new URLSearchParams();
                    if (customerDetails.customer?.user_id) params.set('user_id', customerDetails.customer.user_id);
                    if (customerDetails.customer?.id) params.set('customer_id', customerDetails.customer.id);
                    if (customerDetails.customer?.display_name) params.set('name', customerDetails.customer.display_name);
                    if (customerDetails.customer?.phone) params.set('phone', customerDetails.customer.phone);
                    history.push(`/app/customers/new-booking?${params.toString()}`);
                    toggle();
                  }}
                >
                  <i className="fa fa-plus"></i> New Booking
                </button>
              </div>

              {/* Customer Information Section */}
              <div className={styles.section}>
                <div className={styles.sectionHeader}>
                  <i className={`fa fa-info-circle ${styles.sectionIcon}`}></i>
                  <h5 className={styles.sectionTitle}>Customer Information</h5>
                </div>
                {isEditing ? (
                  <div className={styles.editForm}>
                    <div className={styles.formGroup}>
                      <label className={styles.formLabel}>Name</label>
                      <input
                        type="text"
                        className={styles.formInput}
                        value={editFormData.name}
                        onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                        placeholder="Customer name"
                      />
                      <small className={styles.helpText}>
                        {customerDetails?.customer?.is_b2b
                          ? 'B2B business name (synced with devbusiness profile).'
                          : 'Optional personal contact name.'}
                      </small>
                    </div>
                    <div className={styles.formGroup}>
                      <label className={styles.formLabel}>Business Name</label>
                      <input
                        type="text"
                        className={styles.formInput}
                        value={editFormData.business_name}
                        onChange={(e) => setEditFormData({ ...editFormData, business_name: e.target.value })}
                        placeholder="e.g., Sri Lakshmi Traders"
                      />
                      {customerDetails?.customer?.is_b2b && (
                        <small className={styles.helpText}>
                          B2B legal name (synced with devbusiness registered legal name).
                        </small>
                      )}
                    </div>
                    {customerDetails?.customer?.non_whatsapp_phone ? (
                      <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Non-WA number</label>
                        <input
                          type="text"
                          className={styles.formInput}
                          value={customerDetails.customer.non_whatsapp_phone}
                          disabled
                          readOnly
                          style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                        />
                      </div>
                    ) : null}
                    <div className={styles.formGroup}>
                      <label className={styles.formLabel}>Phone (WhatsApp)</label>
                      <input
                        type="text"
                        className={styles.formInput}
                        value={
                          isEditing && customerDetails?.customer?.is_b2b
                            ? editFormData.phone
                            : customerDetails.customer?.phone
                        }
                        disabled={!isEditing || !customerDetails?.customer?.is_b2b}
                        onChange={(e) =>
                          isEditing &&
                          customerDetails?.customer?.is_b2b &&
                          setEditFormData({ ...editFormData, phone: e.target.value })
                        }
                        style={
                          !isEditing || !customerDetails?.customer?.is_b2b
                            ? { backgroundColor: '#f5f5f5', cursor: 'not-allowed' }
                            : undefined
                        }
                      />
                      <small className={styles.helpText}>
                        {customerDetails?.customer?.is_b2b
                          ? 'Synced with B2B portal WhatsApp number (10-digit mobile).'
                          : 'Phone number cannot be changed'}
                      </small>
                    </div>
                    {additionalNumbers}
                    <div className={styles.formGroup}>
                      <label className={styles.formLabel}>Preferred Language</label>
                      <div className={styles.languageControlRow}>
                        <select
                          className={styles.formInput}
                          value={
                            isPreferredLanguageOption(editFormData.preferred_language)
                              ? String(editFormData.preferred_language).toLowerCase()
                              : '__custom__'
                          }
                          onChange={(e) => {
                            const selected = e.target.value;
                            if (selected === '__custom__') {
                              setUseCustomLanguageInput(true);
                              if (isPreferredLanguageOption(editFormData.preferred_language)) {
                                setEditFormData({ ...editFormData, preferred_language: '' });
                              }
                            } else {
                              setUseCustomLanguageInput(false);
                              setEditFormData({ ...editFormData, preferred_language: selected });
                            }
                          }}
                        >
                          <option value="">Select language</option>
                          <option value="english">english</option>
                          <option value="kannada">kannada</option>
                          <option value="roman_kannada">roman_kannada</option>
                          <option value="__custom__">Custom (type below)</option>
                        </select>
                      </div>

                      {(useCustomLanguageInput || !isPreferredLanguageOption(editFormData.preferred_language)) && (
                        <div className={styles.languageControlRow} style={{ marginTop: '0.5rem' }}>
                          <input
                            type="text"
                            className={styles.formInput}
                            value={editFormData.preferred_language}
                            onChange={(e) => setEditFormData({ ...editFormData, preferred_language: e.target.value })}
                            placeholder="Type custom language"
                          />
                        </div>
                      )}

                    <small className={styles.helpText}>Choose a language, or select “Custom” and type directly.</small>
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Business Category</label>
                    <input
                      type="text"
                      className={styles.formInput}
                      value={editFormData.business_category}
                      onChange={(e) => setEditFormData({
                        ...editFormData, 
                        business_category: e.target.value,
                        domain: '',
                        matched_category: ''
                      })}
                      placeholder="e.g., Retail, Wholesale, etc."
                    />
                    <small className={styles.helpText}>If domain is left as auto, it will be inferred from this category.</small>
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Domain</label>
                    <select
                      className={styles.formInput}
                      value={isDomainOption(editFormData.domain) ? editFormData.domain : ''}
                      onChange={(e) => setEditFormData({...editFormData, domain: e.target.value, matched_category: ''})}
                    >
                      <option value="">Auto-resolve from business category</option>
                      {(Object.keys(taxonomyData).length > 0 ? Object.keys(taxonomyData).sort() : DOMAIN_OPTIONS).map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                    {!isDomainOption(editFormData.domain) && !!editFormData.domain && (
                      <small className={styles.helpText}>Current value "{editFormData.domain}" is legacy; choose a standard domain to normalize it.</small>
                    )}
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Matched Category (Sub-category)</label>
                    <select
                      className={styles.formInput}
                      value={editFormData.matched_category}
                      onChange={(e) => setEditFormData({...editFormData, matched_category: e.target.value})}
                    >
                      <option value="">{editFormData.domain ? 'Auto sync' : 'Pick a domain first, or auto sync both'}</option>
                      {editFormData.domain && taxonomyData[editFormData.domain] && [...taxonomyData[editFormData.domain]].sort().map((sub) => (
                        <option key={sub} value={sub}>{sub}</option>
                      ))}
                    </select>
                    <small className={styles.helpText}>Manual picks here will be remembered by the AI for future customers with similar categories.</small>
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Preloaded Discount (%)</label>
                    <input
                      type="number"
                      className={styles.formInput}
                      min="0"
                      max="100"
                      step="0.01"
                      value={editFormData.discount_percentage}
                      onChange={(e) =>
                        setEditFormData({
                          ...editFormData,
                          discount_percentage: e.target.value,
                        })
                      }
                      placeholder="Leave empty for no customer discount"
                    />
                    <small className={styles.helpText}>
                      This is the customer-level one-time discount preloaded by ops/marketing.
                    </small>
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>GSTIN</label>
                    <input
                      type="text"
                      className={styles.formInput}
                      value={editFormData.gstin}
                      onChange={(e) =>
                        setEditFormData({
                          ...editFormData,
                          gstin: e.target.value,
                        })
                      }
                      placeholder="e.g., 29AADCU9202C1ZH"
                      maxLength={15}
                    />
                    <small className={styles.helpText}>
                      Customer's GST Identification Number. When set, 5% GST (2.5% SGST + 2.5% CGST) is applied to pricing.
                    </small>
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Remarks</label>
                    <textarea
                      className={styles.formInput}
                      rows={3}
                      value={editFormData.remarks}
                      onChange={(e) => setEditFormData({...editFormData, remarks: e.target.value})}
                      placeholder="Internal notes about this customer"
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Default Materials</label>
                    <input
                      type="text"
                      className={styles.formInput}
                      value={editFormData.default_material_description}
                      onChange={(e) => setEditFormData({...editFormData, default_material_description: e.target.value})}
                      placeholder="e.g. Cement bags, Furniture items, Steel rods"
                    />
                    <small className={styles.helpText}>Pre-filled when this customer books. Updated automatically from bookings.</small>
                  </div>
                  <div className={styles.formGroup}>
                    <label className={styles.formLabel}>Important Customer</label>
                    <div>
                      <input
                        id="edit-customer-star"
                        type="checkbox"
                        checked={!!editFormData.star}
                        onChange={(e) => setEditFormData({...editFormData, star: e.target.checked})}
                      />
                      <label htmlFor="edit-customer-star" style={{ marginLeft: '8px', marginBottom: 0 }}>
                        Mark as important customer
                      </label>
                    </div>
                  </div>
                  <div className={styles.formGroup}>
                     <label className={styles.formLabel}>Primary Address (optional)</label>
                    {customerDetails?.customer?.is_b2b && (
                      <small className={styles.helpText} style={{ display: 'block', marginBottom: '0.35rem' }}>
                        Synced with devbusiness registered office address.
                      </small>
                    )}
                    <div className={styles.addressSearchContainer}>
                      <input
                        type="text"
                        className={styles.formInput}
                        value={addressInput}
                        onChange={(e) => setAddressInput(e.target.value)}
                        placeholder="Search for address (locality, city)"
                        onKeyPress={(e) => e.key === 'Enter' && handleResolveAddress()}
                      />
                      <button
                        type="button"
                        className={styles.searchAddressBtn}
                        onClick={handleResolveAddress}
                        disabled={addressLoading || !addressInput.trim()}
                      >
                        {addressLoading ? (
                          <><i className="fa fa-spinner fa-spin"></i> Searching...</>
                        ) : (
                          <><i className="fa fa-search"></i> Search</>
                        )}
                      </button>
                    </div>
                    
                    {addressSelection && (
                      <div className={styles.addressSelected}>
                        <i className="fa fa-check-circle"></i>
                        <div className={styles.addressSelectedText}>
                          <strong>{addressSelection.text}</strong>
                          <small>Address ID: {addressSelection.id}</small>
                        </div>
                      </div>
                    )}
                    
                    {addressSuggestions.length > 0 && (
                      <div className={styles.addressSuggestions}>
                        <div className={styles.suggestionsHeader}>
                          <i className="fa fa-info-circle"></i> Multiple matches found — click one to select
                        </div>
                        {addressSuggestions.map((suggestion, idx) => (
                          <div
                            key={idx}
                            className={`${styles.suggestionItem} ${addressLoading ? styles.suggestionItemDisabled : ''}`}
                            onClick={() => !addressLoading && handleAddressSuggestionSelect(suggestion)}
                          >
                            <div className={styles.suggestionMain}>
                              <i className="fa fa-map-marker"></i>
                              <strong>{suggestion.locality}</strong>
                              {suggestion.source === 'google_suggest' && (
                                <span className={styles.googleBadge} title="From Google Maps — not yet in our database">
                                  🌐 Suggested
                                </span>
                              )}
                            </div>
                            <div className={styles.suggestionDetails}>
                              {[suggestion.city, suggestion.state, suggestion.pin_code].filter(Boolean).join(', ')}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {!addressSelection && customerDetails.primary_address && (
                      <small className={styles.helpText}>
                        Current: {customerDetails.primary_address.locality}, {customerDetails.primary_address.city}
                      </small>
                    )}
                  </div>
                </div>
              ) : (
                <>
                  <table className={styles.infoTable}>
                    <tbody>
                      <InfoRow label="Customer ID" value={customerDetails.customer?.id} />
                      <InfoRow label="Name" value={customerDetails.customer?.name} />
                      <InfoRow label="Business Name" value={customerDetails.customer?.business_name || 'Not set'} />
                      <InfoRow label="Phone">
                        <div>{customerDetails.customer?.phone || 'N/A'}</div>
                        {additionalNumbers}
                      </InfoRow>
                      <InfoRow label="Preferred Language" value={customerDetails.customer?.preferred_language || 'Not set'} />
                      <InfoRow label="Business Category" value={customerDetails.customer?.business_category || 'Not set'} />
                      <InfoRow label="Matched Category" value={customerDetails.customer?.matched_category || 'Not set'} />
                      <InfoRow label="Domain" value={customerDetails.customer?.domain || 'Not set'} />
                      <InfoRow
                        label="Preloaded Discount"
                        value={
                          customerDetails.customer?.discount_percentage != null
                            ? `${customerDetails.customer.discount_percentage}%`
                            : 'Not set'
                        }
                      />
                      <InfoRow
                        label="GSTIN"
                        value={customerDetails.customer?.gstin || 'Not set'}
                      />
                      <InfoRow 
                        label="Primary Address" 
                        value={
                          customerDetails.primary_address 
                            ? `${customerDetails.primary_address.locality}, ${customerDetails.primary_address.city}`
                            : 'Not set'
                        } 
                      />
                      <InfoRow 
                        label="Total Bookings" 
                        renderValue={() => (
                          <span className={`${styles.badge} ${styles.primary}`}>
                            <i className="fa fa-calendar"></i>
                            {customerDetails.bookings?.length || 0}
                          </span>
                        )}
                      />
                      <InfoRow label="Joined on" value={formatDateTime(customerDetails.customer?.created_at)} />
                    </tbody>
                  </table>

                    <div
                      className={`${styles.remarksCallout} ${customerDetails.customer?.remarks ? styles.hasRemarks : styles.noRemarks}`}
                      onClick={!isRemarksEditing && !isEditing ? handleOpenRemarksEditor : undefined}
                    >
                      <div className={styles.remarksHeader}>
                        <div>
                          <i className="fa fa-sticky-note"></i>
                          <span>Remarks</span>
                        </div>
                        {!isEditing && !isRemarksEditing && (
                          <button
                            type="button"
                            className={styles.remarksActionBtn}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenRemarksEditor();
                            }}
                          >
                            <i className="fa fa-pencil"></i> Edit
                          </button>
                        )}
                      </div>
                      {isRemarksEditing ? (
                        <>
                          <textarea
                            className={styles.remarksEditorTextarea}
                            rows={3}
                            value={remarksDraft}
                            onChange={(e) => setRemarksDraft(e.target.value)}
                            placeholder="Add remarks"
                            onClick={(e) => e.stopPropagation()}
                          />
                          <div className={styles.remarksEditorActions} onClick={(e) => e.stopPropagation()}>
                            <button
                              type="button"
                              className={styles.remarksSaveBtn}
                              onClick={handleSaveRemarks}
                              disabled={remarksSaving}
                            >
                              <i className={`fa ${remarksSaving ? 'fa-spinner fa-spin' : 'fa-check'}`}></i>
                              {remarksSaving ? 'Saving...' : 'Save Remarks'}
                            </button>
                            <button
                              type="button"
                              className={styles.remarksCancelBtn}
                              onClick={handleCancelRemarksEdit}
                              disabled={remarksSaving}
                            >
                              <i className="fa fa-times"></i> Cancel
                            </button>
                          </div>
                        </>
                      ) : (
                        <>
                          <div className={styles.remarksText}>
                            {customerDetails.customer?.remarks || 'No remarks added yet.'}
                          </div>
                          {!isEditing && (
                            <div className={styles.remarksHint}>Click to edit remarks</div>
                          )}
                        </>
                      )}
                    </div>

                    {/* Default Materials */}
                    {customerDetails.customer?.default_material_description && (
                      <div className={`${styles.remarksCallout} ${styles.hasRemarks}`}>
                        <div className={styles.remarksHeader}>
                          <div>
                            <i className="fa fa-cube"></i>
                            <span>Default Materials</span>
                          </div>
                        </div>
                        <div className={styles.remarksText}>
                          {customerDetails.customer.default_material_description}
                        </div>
                      </div>
                    )}

                    {/* Call History - Tabbed Section */}
                    <div className={styles.callHistorySection}>
                      <div className={styles.callHistoryHeader}>
                        <div>
                          <i className="fa fa-phone" />
                          <span>Call History</span>
                        </div>
                      </div>
                      <div className={styles.callHistoryTabs}>
                        <button
                          type="button"
                          className={`${styles.callTab} ${activeCallTab === 'bot' ? styles.callTabActive : ''}`}
                          onClick={() => setActiveCallTab('bot')}
                        >
                          <i className="fa fa-android" /> Voice Bot
                          {(() => {
                            const mktCount = voiceMktTotal || voiceMktSummaries.length;
                            const crmCount = parseVoiceCallEntries(customerDetails.customer?.call_summary).length;
                            const count = mktCount || crmCount;
                            return count > 0 ? <span className={styles.callTabCount}>{count}</span> : null;
                          })()}
                        </button>
                        <button
                          type="button"
                          className={`${styles.callTab} ${activeCallTab === 'operator' ? styles.callTabActive : ''}`}
                          onClick={() => setActiveCallTab('operator')}
                        >
                          <i className="fa fa-headphones" /> Operator Calls
                          {operatorCallLogsTotal > 0 && (
                            <span className={styles.callTabCount}>{operatorCallLogsTotal}</span>
                          )}
                        </button>
                      </div>

                      {/* Voice Bot Tab — same final Vobiz / Tracker format as Voice Tracker */}
                      {activeCallTab === 'bot' && (
                        <div className={styles.callHistoryTabContent}>
                          {(() => {
                            const crmEntries = parseVoiceCallEntries(
                              customerDetails.customer?.call_summary,
                            );
                            const useMkt = voiceMktSummaries.length > 0;
                            const count = useMkt ? (voiceMktTotal || voiceMktSummaries.length) : crmEntries.length;

                            if (voiceMktLoading && !useMkt && crmEntries.length === 0) {
                              return (
                                <div className={styles.callHistoryLoading}>
                                  <div className={styles.spinner} style={{ width: '28px', height: '28px' }} />
                                </div>
                              );
                            }

                            if (!useMkt && crmEntries.length === 0) {
                              return (
                                <div className={styles.callHistoryEmpty}>
                                  {voiceMktError
                                    ? 'No voice bot calls recorded yet.'
                                    : 'No voice bot calls recorded yet.'}
                                </div>
                              );
                            }

                            if (useMkt) {
                              const displayed = voiceMktSummaries;
                              return (
                                <>
                                  {voiceMktError && (
                                    <div style={{ color: '#b45309', fontSize: '0.78rem', marginBottom: '0.5rem' }}>
                                      Tracker feed unavailable — showing marketing logs when possible.
                                    </div>
                                  )}
                                  <ul className={styles.callSummaryList}>
                                    {displayed.map((row, index) => {
                                      const parts = buildTrackerStyleSummary(row);
                                      return (
                                        <CallSummaryItem
                                          key={`mkt-${row.id || index}-${row.time || ''}`}
                                          entry={{ formatted: row.time || '', time: row.time || '' }}
                                          trackerParts={parts}
                                        />
                                      );
                                    })}
                                  </ul>
                                  {(voiceMktHasMore || count > displayed.length) && (
                                    <button
                                      className={styles.loadAllSummariesBtn}
                                      onClick={() => setShowAllSummaries(!showAllSummaries)}
                                    >
                                      <i className={`fa fa-chevron-${showAllSummaries ? 'up' : 'down'}`} />
                                      {showAllSummaries ? 'Show less' : `Load all (${count} summaries)`}
                                    </button>
                                  )}
                                </>
                              );
                            }

                            const reversedEntries = [...crmEntries].reverse();
                            const hasMore = reversedEntries.length > 5;
                            const displayedEntries = (hasMore && !showAllSummaries)
                              ? reversedEntries.slice(0, 5)
                              : reversedEntries;

                            return (
                              <>
                                {voiceMktError && (
                                  <div style={{ color: '#64748b', fontSize: '0.75rem', marginBottom: '0.5rem' }}>
                                    Showing CRM copies (Voice Tracker API unreachable).
                                  </div>
                                )}
                                <ul className={styles.callSummaryList}>
                                  {displayedEntries.map((entry, index) => (
                                    <CallSummaryItem
                                      key={`${entry.timestamp}-${entry.raw}-${index}`}
                                      entry={entry}
                                      onDelete={handleDeleteCallSummary}
                                      isDeleting={deletingSummary}
                                      trackerParts={buildTrackerStyleFromCrmEntry(entry)}
                                    />
                                  ))}
                                </ul>
                                {hasMore && (
                                  <button
                                    className={styles.loadAllSummariesBtn}
                                    onClick={() => setShowAllSummaries(!showAllSummaries)}
                                  >
                                    <i className={`fa fa-chevron-${showAllSummaries ? 'up' : 'down'}`} />
                                    {showAllSummaries ? 'Show less' : `Load all (${reversedEntries.length} summaries)`}
                                  </button>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      )}

                      {/* Operator Calls Tab */}
                      {activeCallTab === 'operator' && (
                        <div className={styles.callHistoryTabContent}>
                          {operatorCallLogsLoading && (
                            <div className={styles.callHistoryLoading}>
                              <div className={styles.spinner} style={{ width: '28px', height: '28px' }}></div>
                              <span>Loading call logs...</span>
                            </div>
                          )}
                          {operatorCallLogsError && (
                            <div className={styles.callHistoryError}>
                              <i className="fa fa-exclamation-triangle"></i>
                              {operatorCallLogsError}
                            </div>
                          )}
                          {!operatorCallLogsLoading && !operatorCallLogsError && operatorCallLogs.length === 0 && (
                            <div className={styles.callHistoryEmpty}>
                              No operator calls recorded for this customer.
                            </div>
                          )}
                          {!operatorCallLogsLoading && !operatorCallLogsError && operatorCallLogs.length > 0 && (
                            <>
                              <ul className={styles.operatorCallList}>
                                {operatorCallLogs.map((log) => (
                                  <OperatorCallLogItem
                                    key={`${log.entry_kind || 'call_log'}-${log.id}`}
                                    log={log}
                                    customerId={customerId}
                                  />
                                ))}
                              </ul>
                              {onViewAllCalls && (
                                <button
                                  className={styles.loadAllSummariesBtn}
                                  onClick={() => onViewAllCalls(customerDetails?.customer?.phone)}
                                >
                                  <i className="fa fa-external-link" />
                                  View all {operatorCallLogsTotal} calls
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* Active Leads Section */}
              <div className={styles.section}>
                <div className={styles.sectionHeader}>
                  <i className={`fa fa-inbox ${styles.sectionIcon}`}></i>
                  <h5 className={styles.sectionTitle}>Active Leads</h5>
                  {!activeLeadsLoading && !activeLeadsError && (
                    <span className={styles.sectionBadge}>{activeLeadsTotal}</span>
                  )}
                </div>

                {activeLeadsLoading && (
                  <div style={{ textAlign: 'center', padding: '1.5rem' }}>
                    <div className={styles.spinner} style={{ width: '32px', height: '32px' }}></div>
                  </div>
                )}

                {activeLeadsError && (
                  <div style={{ padding: '1rem', background: '#fff8e1', borderRadius: '8px', color: '#f57f17' }}>
                    <i className="fa fa-exclamation-triangle" style={{ marginRight: '0.5rem' }}></i>
                    Failed to load active leads: {activeLeadsError}
                  </div>
                )}

                {!activeLeadsLoading && !activeLeadsError && activeLeads.length === 0 && (
                  <div className={styles.emptyState}>
                    <div className={styles.emptyIcon}>
                      <i className="fa fa-inbox"></i>
                    </div>
                    <p>No active leads for this customer</p>
                  </div>
                )}

                {!activeLeadsLoading && !activeLeadsError && activeLeads.length > 0 && (
                  <div>
                    <p className={styles.helperText}>
                      <i className="fa fa-info-circle"></i>
                      Click a lead to see more, or open it in the Leads page
                    </p>
                    {activeLeads.map((lead) => (
                      <LeadCard key={lead.id} lead={lead} history={history} onOpenLead={handleOpenLead} />
                    ))}
                    {activeLeads.length < activeLeadsTotal && (
                      <button
                        type="button"
                        className={styles.showMoreBtn}
                        onClick={loadAllLeads}
                        disabled={activeLeadsLoading}
                      >
                        <i className={`fa ${activeLeadsLoading ? 'fa-spinner fa-spin' : 'fa-chevron-down'}`}></i>
                        Show all leads ({activeLeadsTotal - activeLeads.length} more)
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Bookings Section */}
              <div className={styles.section}>
                <div className={styles.sectionHeader}>
                  <i className={`fa fa-calendar ${styles.sectionIcon}`}></i>
                  <h5 className={styles.sectionTitle}>Recent Bookings</h5>
                  {customerDetails.bookings && (
                    <span className={styles.sectionBadge}>{customerDetails.bookings.length}</span>
                  )}
                </div>

                {customerDetails.bookings && customerDetails.bookings.length > 0 ? (
                  <div>
                    <p className={styles.helperText}>
                      <i className="fa fa-info-circle"></i>
                      Click on any booking to view detailed information
                    </p>
                    {(showAllBookings
                      ? customerDetails.bookings
                      : customerDetails.bookings.slice(0, 10)
                    ).filter(booking => {
                      const bookingDate = booking.pickup_time || booking.created_at;
                      return !bookingDate || isDateInRange(bookingDate);
                    }).map((booking) => (
                      <BookingCard key={booking.id} booking={booking} />
                    ))}

                    {customerDetails.bookings.length > 10 && (
                      <button
                        className={styles.showMoreBtn}
                        onClick={() => setShowAllBookings(!showAllBookings)}
                      >
                        <i className={`fa fa-${showAllBookings ? 'chevron-up' : 'chevron-down'}`}></i>
                        {showAllBookings ? 'Show Less' : `Show All ${customerDetails.bookings.length} Bookings`}
                      </button>
                    )}
                  </div>
                ) : (
                  <div className={styles.emptyState}>
                    <div className={styles.emptyIcon}>
                      <i className="fa fa-calendar-o"></i>
                    </div>
                    <p>No bookings found for this customer</p>
                  </div>
                )}
              </div>

              {/* Conversations Section */}
              <div className={styles.section}>
                <div className={styles.sectionHeader}>
                  <i className={`fa fa-comments ${styles.sectionIcon}`}></i>
                  <h5 className={styles.sectionTitle}>Customer Conversations</h5>
                  {conversationsTotal > 0 && (
                    <span className={styles.sectionBadge}>{conversationsTotal}</span>
                  )}
                </div>

                {conversationsLoading && (
                  <div style={{ textAlign: 'center', padding: '1.5rem' }}>
                    <div className={styles.spinner} style={{ width: '32px', height: '32px' }}></div>
                  </div>
                )}

                {conversationsError && (
                  <div style={{ padding: '1rem', background: '#fff8e1', borderRadius: '8px', color: '#f57f17' }}>
                    <i className="fa fa-exclamation-triangle" style={{ marginRight: '0.5rem' }}></i>
                    Failed to load conversations: {conversationsError}
                  </div>
                )}

                {!conversationsLoading && !conversationsError && whatsAppTimeline.length > 0 ? (
                  <div>
                    <p className={styles.helperText}>
                      <i className="fa fa-info-circle"></i>
                      Chat sessions with the booking bot
                    </p>
                    {whatsAppTimeline.map((item) => {
                      const conversation = item.data;
                      const relatedBooking = conversation.booking_id
                        ? customerDetails.bookings?.find(b => b.id === conversation.booking_id)
                        : null;
                      return (
                        <ConversationCard
                          key={conversation.id}
                          conversation={conversation}
                          relatedBooking={relatedBooking}
                          history={history}
                          customerId={customerDetails.customer?.id}
                          onOpenConversation={(conversationId, cid) =>
                            openChat({ conversationId, customerId: cid })
                          }
                        />
                      );
                    })}
                    {whatsAppTimeline.length < conversationsTotal && (
                      <button
                        type="button"
                        className={styles.showMoreBtn}
                        onClick={loadAllConversations}
                        disabled={conversationsLoading}
                      >
                        <i className={`fa ${conversationsLoading ? 'fa-spinner fa-spin' : 'fa-chevron-down'}`}></i>
                        Show all ({conversationsTotal - whatsAppTimeline.length} more)
                      </button>
                    )}
                  </div>
                ) : (
                  !conversationsLoading && !conversationsError && (
                    <div className={styles.emptyState}>
                      <div className={styles.emptyIcon}>
                        <i className="fa fa-comments-o"></i>
                      </div>
                      <p>No conversations found for this customer</p>
                    </div>
                  )
                )}
              </div>

            </>
          )}

          {/* No Customer Selected State */}
          {!loading && !error && !customerDetails && (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>
                <i className="fa fa-inbox"></i>
              </div>
              <p>Select a customer to view details</p>
            </div>
          )}

        </ModalBody>
      </Modal>

    </>
  );
};

export default CustomerDetailsModal;
